/* eslint-disable */
import { useState, useRef, useEffect } from "react";
import { supabase } from './supabase';

// ─── Palette ──────────────────────────────────────────────────────────────────
const C = {
  bg:"#0f0f0f", card:"#1a1a1a", border:"#2a2a2a",
  brand:"#e8651a", text:"#f0f0f0", sub:"#888", faint:"#444",
  gold:"#d4a844", green:"#4caf7d", red:"#e05252", wa:"#25D366",
};
// Images used by more than one artist are Ticketmaster's generic placeholders

function dedupeImages(list){
  const users = {};
  list.forEach(s=>{ if(s.image){ (users[s.image]=users[s.image]||new Set()).add(s.attrId); } });
  return list.map(s=> s.image && users[s.image].size>1 ? {...s, image:null} : s);
}
const PLACEHOLDER_COLORS = [["#e8651a","#7a2e0b"],["#8b5cf6","#3b1f7a"],["#0ea5e9","#0b3b66"],["#10b981","#064e3b"],["#ec4899","#6b1236"],["#f59e0b","#6b3b04"]];
function placeholderBg(name){
  let h=0; for(const ch of (name||"?")) h=(h*31+ch.charCodeAt(0))%1000;
  const [a,b]=PLACEHOLDER_COLORS[h%PLACEHOLDER_COLORS.length];
  return `linear-gradient(135deg,${a},${b})`;
}
function initials(name){
  const w=(name||"?").replace(/[^\p{L}\p{N} ]/gu,"").split(" ").filter(Boolean);
  return ((w[0]?.[0]||"?")+(w[1]?.[0]||"")).toUpperCase();
}

function ArtistBanner({ name, image }){
  return image
    ? <img src={image} alt={name} style={{display:"block",width:"100%",height:170,objectFit:"cover",borderRadius:12,marginBottom:14}}/>
    : <div style={{display:"flex",alignItems:"center",justifyContent:"center",width:"100%",height:170,borderRadius:12,marginBottom:14,background:placeholderBg(name),color:"rgba(255,255,255,0.9)",fontSize:52,fontWeight:700,letterSpacing:2}}>{initials(name)}</div>;
}

const AVATAR_EMOJIS = ["🎸","🥁","🎤","🎹","🎷","🎺","🎻","🎧","🪩","🔥","⚡","🌈"];
const AVATAR_COLORS = ["#e8651a","#8b5cf6","#0ea5e9","#10b981","#ec4899","#f59e0b"];
function AvatarSheet({ current, onSave, onClose }){
  const [emoji,setEmoji] = useState(current?.emoji||"🎸");
  const [color,setColor] = useState(current?.color||AVATAR_COLORS[0]);
  return (
    <Sheet title="Your avatar" onClose={onClose}>
      <div style={{display:"flex",justifyContent:"center",margin:"4px 0 18px"}}>
        <div style={{width:88,height:88,borderRadius:"50%",background:color,display:"flex",alignItems:"center",justifyContent:"center",fontSize:42}}>{emoji}</div>
      </div>
      <div style={{display:"flex",flexWrap:"wrap",gap:10,justifyContent:"center",marginBottom:18}}>
        {AVATAR_EMOJIS.map(e=>(
          <button key={e} onClick={()=>setEmoji(e)} style={{width:48,height:48,borderRadius:12,fontSize:24,cursor:"pointer",background:emoji===e?`${C.brand}33`:"none",border:`2px solid ${emoji===e?C.brand:C.border}`}}>{e}</button>
        ))}
      </div>
      <div style={{display:"flex",gap:12,justifyContent:"center",marginBottom:22}}>
        {AVATAR_COLORS.map(c=>(
          <button key={c} onClick={()=>setColor(c)} style={{width:34,height:34,borderRadius:"50%",background:c,cursor:"pointer",border:color===c?"3px solid #fff":"3px solid transparent"}}/>
        ))}
      </div>
      <button onClick={()=>onSave({emoji,color})} style={{width:"100%",background:C.brand,border:"none",borderRadius:12,padding:"14px",color:"#fff",fontSize:15,fontWeight:700,cursor:"pointer"}}>Save</button>
    </Sheet>
  );
}

function daysUntil(dateStr) {
  const today = new Date();
  today.setHours(0,0,0,0);
  const eventDate = new Date(dateStr);
  eventDate.setHours(0,0,0,0);
  const diffTime = eventDate - today;
  return Math.round(diffTime / (1000 * 60 * 60 * 24));
}

// ─── Push notification helper ─────────────────────────────────────────────────
function urlBase64ToUint8Array(base64String){
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for(let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

// ─── Pro state (global sim) ───────────────────────────────────────────────────
const OWNER_IDS = [
  "6ee4df85-59a2-489e-80d9-d25f65d4b0fb", // Bruno
  "b9e783c5-816f-404c-8700-25243f9a1e69",  // Daniele 🧡
];
let _isPro = true; // everything is free at launch
let _isSuperfan = false;
let _proListeners = [];
const setProGlobal = (v) => { _isPro = v; _proListeners.forEach(fn => fn(v)); };
function useIsPro() {
  const [pro, setPro] = useState(_isPro);
  useEffect(() => {
    // Owner always gets Superfan for free 🎤✦
    supabase.auth.getSession().then(({ data: { session } })=>{
      if(OWNER_IDS.includes(session?.user?.id)){ setProGlobal(true); _isSuperfan = true; }
    });
    _proListeners.push(setPro);
    return () => { _proListeners = _proListeners.filter(f => f !== setPro); };
  }, []);
  return pro;
}

// ─── Data ─────────────────────────────────────────────────────────────────────
const MY_SHOWS = [
  { id:1, artist:"Radiohead", venue:"Scotiabank Arena", date:"2023-07-14", rating:5, city:"Toronto, ON",
    notes:"Opened with Everything in Its Right Place. Absolute perfection.",
    setlist:["Everything in Its Right Place","Karma Police","Paranoid Android","Fake Plastic Trees","Lucky","No Surprises","Creep","Exit Music"],
    grails:["Pyramid Song","Street Spirit"],
    media:[
      { type:"video", title:"Everything in Its Right Place", thumb:"🎬", duration:"5:14", url:"https://www.youtube.com/watch?v=onRk0sjSgFU" },
      { type:"video", title:"Karma Police (live)", thumb:"🎬", duration:"4:51", url:"https://www.youtube.com/watch?v=1uYWYWPc9HU" },
      { type:"audio", title:"No Surprises (live audio)", thumb:"🎵", duration:"3:48", url:"" },
    ]
  },
  { id:2, artist:"Tame Impala", venue:"Budweiser Stage", date:"2023-09-02", rating:4, city:"Toronto, ON",
    notes:"Sunset set was unreal. The visuals during Let It Happen were insane.",
    setlist:["Let It Happen","Elephant","The Less I Know the Better","Eventually","Feels Like We Only Go Backwards","New Person, Same Old Mistakes"],
    grails:["Apocalypse Dreams"],
    media:[
      { type:"video", title:"Let It Happen (live)", thumb:"🎬", duration:"7:46", url:"https://www.youtube.com/watch?v=pFptt7Cargc" },
      { type:"audio", title:"Elephant (live audio)", thumb:"🎵", duration:"4:51", url:"" },
    ]
  },
  { id:3, artist:"Phoebe Bridgers", venue:"Massey Hall", date:"2024-06-21", rating:5, city:"Toronto, ON",
    notes:"She played Savior Complex. I cried.",
    setlist:["Motion Sickness","Savior Complex","Garden Song","Moon Song","Funeral","Kyoto","Graceland Too"],
    grails:[],
    media:[
      { type:"video", title:"Savior Complex (live)", thumb:"🎬", duration:"4:22", url:"https://www.youtube.com/watch?v=2PMOjHcv36M" },
      { type:"video", title:"Moon Song (live)", thumb:"🎬", duration:"3:18", url:"" },
    ]
  },
];

const UPCOMING = [
  { id:"u1", artist:"Radiohead", venue:"Scotiabank Arena", city:"Toronto, ON", date:"2026-06-14", low:105, high:380, avg:162, trend:"rising", pct:12, grailChance:72, friends:["Maya","Jordan"],
    venueType:"arena",
    concertSetlist:["Burn the Witch","Daydreaming","Ful Stop","Glass Eyes","Decks Dark","Desert Island Disk","Identikit","The Numbers","Present Tense","Tinker Tailor","True Love Waits"] },
  { id:"u2", artist:"Tame Impala", venue:"Budweiser Stage", city:"Toronto, ON", date:"2026-05-22", low:65, high:230, avg:110, trend:"stable", pct:0, grailChance:45, friends:["Sam"],
    venueType:"amphitheater",
    concertSetlist:["Let It Happen","Elephant","The Less I Know the Better","Eventually","Breathe Deeper","On Track","Is It True","Lost in Yesterday","It Might Be Time","Posthumous Forgiveness"] },
  { id:"u3", artist:"Bon Iver", venue:"Massey Hall", city:"Toronto, ON", date:"2026-07-03", low:120, high:480, avg:210, trend:"falling", pct:-8, grailChance:0, friends:[],
    venueType:"theater",
    concertSetlist:["22 (OVER S∞∞N)","Hey, Ma","Holocene","Towers","Skinny Love","Perth","Wash.","Michicant","Lump Sum","Wolves (Act I and II)"] },
  { id:"u4", artist:"Mitski", venue:"History", city:"Toronto, ON", date:"2026-05-18", low:48, high:140, avg:75, trend:"rising", pct:22, grailChance:0, friends:["Maya"],
    venueType:"club",
    concertSetlist:["Washing Machine Heart","Nobody","First Love / Late Spring","Your Best American Girl","Geyser","A Pearl","Drunk Walk Home","Last Words of a Shooting Star"] },
  { id:"u5", artist:"LCD Soundsystem", venue:"Scotiabank Arena", city:"Toronto, ON", date:"2026-07-19", low:99, high:390, avg:175, trend:"rising", pct:18, grailChance:0, friends:["Maya","Jordan","Sam"],
    venueType:"arena",
    concertSetlist:["Dance Yrself Clean","Drunk Girls","I Can Change","All I Want","Someone Great","All My Friends","New York, I Love You But You're Bringing Me Down","Losing My Edge"] },
];

const FRIENDS = [
  { id:"f1", name:"Maya Chen", avatar:"MC", service:"Spotify", shows:3, overlap:75, connected:true },
  { id:"f2", name:"Jordan Park", avatar:"JP", service:"Apple Music", shows:2, overlap:50, connected:true },
  { id:"f3", name:"Sam Rivera", avatar:"SR", service:"Spotify", shows:1, overlap:25, connected:false },
];

const VENUE_SECTIONS = {
  arena:[ {id:"floor",label:"Floor / Pit",price:189,avail:"low",desc:"Standing only, closest to stage"}, {id:"lower",label:"Lower Bowl",price:147,avail:"med",desc:"Sections 101–120, great sightlines"}, {id:"upper",label:"Upper Deck",price:89,avail:"hi",desc:"Full view of stage and crowd"}, {id:"vip",label:"VIP Box",price:340,avail:"low",desc:"Private suite with lounge access"} ],
  amphitheater:[ {id:"pit",label:"GA Pit",price:98,avail:"low",desc:"Standing, front of stage"}, {id:"orch",label:"Orchestra",price:155,avail:"med",desc:"Reserved seating, centre view"}, {id:"lawn",label:"Lawn GA",price:55,avail:"hi",desc:"Open lawn, bring a blanket"}, {id:"box",label:"Premium Box",price:210,avail:"low",desc:"Best seats in the house"} ],
  theater:[ {id:"front",label:"Front Row",price:450,avail:"low",desc:"Row A–C, inches from the stage"}, {id:"orch",label:"Orchestra",price:295,avail:"low",desc:"Rows D–R, prime angle"}, {id:"mezz",label:"Mezzanine",price:195,avail:"med",desc:"Elevated, full stage view"}, {id:"balc",label:"Balcony",price:110,avail:"hi",desc:"Top floor, great acoustics"} ],
  club:[ {id:"barrier",label:"Barrier / Front",price:68,avail:"low",desc:"Right at the rail"}, {id:"floor",label:"General Floor",price:52,avail:"med",desc:"Open standing floor"}, {id:"balc",label:"Balcony",price:42,avail:"hi",desc:"Elevated view, less crowded"}, {id:"vip",label:"VIP Table",price:130,avail:"low",desc:"Reserved with bottle service"} ],
};
const AVAIL_COLOR = { low:C.red, med:C.gold, hi:C.green };
const AVAIL_LABEL = { low:"Few left", med:"Some left", hi:"Available" };

const fmtDate  = d => new Date(d).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});
const fmtShort = d => new Date(d).toLocaleDateString("en-US",{month:"short",day:"numeric"});

// ─── Shared UI ────────────────────────────────────────────────────────────────
function Divider(){ return <div style={{height:1,background:C.border,margin:"0 -20px"}}/>; }

function Stars({ n }){
  return <span style={{color:C.gold,fontSize:14,letterSpacing:1}}>{"★".repeat(n)}{"☆".repeat(5-n)}</span>;
}

function Pill({ children, color }){
  return <span style={{fontSize:11,fontWeight:600,color:color||C.sub,background:`${color||C.sub}18`,border:`1px solid ${color||C.sub}33`,borderRadius:20,padding:"2px 8px",whiteSpace:"nowrap"}}>{children}</span>;
}

function Sheet({ onClose, children, title, tall }){
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:200,display:"flex",alignItems:"flex-end",justifyContent:"center"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:C.card,borderRadius:"20px 20px 0 0",width:"100%",maxWidth:560,maxHeight:tall?"96vh":"88vh",overflowY:"auto",paddingBottom:110}}>
        <div style={{padding:"16px 20px 0",display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <span style={{fontSize:17,fontWeight:700,color:C.text}}>{title}</span>
          <button onClick={onClose} style={{background:"none",border:"none",color:C.sub,fontSize:22,cursor:"pointer",padding:4}}>×</button>
        </div>
        <Divider/>
        <div style={{padding:"16px 20px 0"}}>{children}</div>
      </div>
    </div>
  );
}



// ─── Share Media Sheet ────────────────────────────────────────────────────────
function ShareMediaSheet({ item, show, onClose }){
  const [step, setStep] = useState("pick");
  const [platform, setPlatform] = useState(null);

  const videoUrl = `https://www.youtube.com/watch?v=${item.youtube_id||getYouTubeId(item.url)}`;
  const shareText = `Just watched "${item.title}" from ${show.artist} at ${show.venue}. What a night 🎤\n\nencore-d55e.vercel.app`;

  const PLATFORMS = [
    { id:"whatsapp", label:"WhatsApp", icon:"💬", color:C.wa, desc:"Send to a contact",
      action:()=>window.open(`https://wa.me/?text=${encodeURIComponent(shareText + "\n" + videoUrl)}`, "_blank") },
    { id:"tiktok", label:"TikTok", icon:"♪", color:"#ff0050", desc:"Share to TikTok",
      action:()=>window.open(`https://www.tiktok.com/share?url=${encodeURIComponent(videoUrl)}&text=${encodeURIComponent(shareText)}`, "_blank") },
    { id:"instagram", label:"Instagram", icon:"◈", color:"#e1306c", desc:"Share to Instagram Stories",
      action:()=>{
        // Instagram doesn't support direct URL sharing from web — copy link instead
        navigator.clipboard.writeText(videoUrl + "\n\n" + shareText);
        alert("Link copied! Open Instagram and paste in your story or post 📸");
      }},
    { id:"twitter", label:"X / Twitter", icon:"𝕏", color:"#e0deff", desc:"Post with your take",
      action:()=>window.open(`https://x.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(videoUrl)}`, "_blank") },
    { id:"copy", label:"Copy link", icon:"🔗", color:C.sub, desc:"Paste anywhere",
      action:()=>{ navigator.clipboard.writeText(videoUrl + "\n\n" + shareText); }},
  ];

  const handleShare = (p) => {
    p.action();
    setPlatform(p.id);
    setStep("done");
  };

  if(step==="done") return (
    <Sheet title="Shared! 🎉" onClose={onClose}>
      <div style={{textAlign:"center",padding:"24px 0"}}>
        <div style={{fontSize:48,marginBottom:12}}>{platform==="copy"||platform==="instagram"?"✓":"🎉"}</div>
        <p style={{margin:"0 0 6px",fontSize:17,fontWeight:700,color:C.text}}>
          {platform==="copy"?"Link copied!":platform==="instagram"?"Link copied for Instagram!":"Opened "+PLATFORMS.find(p=>p.id===platform)?.label}
        </p>
        <p style={{margin:"0 0 24px",fontSize:14,color:C.sub}}>
          {platform==="instagram"?"Paste in your Instagram story or post":"Share the love for live music! 🎤"}
        </p>
        <button onClick={onClose} style={{background:C.brand,border:"none",borderRadius:12,padding:"12px 32px",color:"#fff",fontSize:15,fontWeight:700,cursor:"pointer"}}>Done</button>
      </div>
    </Sheet>
  );

  return (
    <Sheet title="Share media" onClose={onClose}>
      <div style={{background:C.bg,borderRadius:12,padding:"12px 14px",marginBottom:20,display:"flex",gap:12,alignItems:"center"}}>
        <div style={{width:40,height:40,borderRadius:8,background:`${C.brand}22`,border:`1px solid ${C.brand}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>
          {item.type==="video"?"▶":"♪"}
        </div>
        <div style={{flex:1,minWidth:0}}>
          <p style={{margin:0,fontSize:14,fontWeight:600,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.title}</p>
          <p style={{margin:0,fontSize:12,color:C.sub}}>{show.artist} · {show.venue}</p>
        </div>
      </div>

      <p style={{margin:"0 0 12px",fontSize:13,color:C.sub}}>Share to</p>
      {PLATFORMS.map(p=>(
        <div key={p.id} onClick={()=>handleShare(p)}
          style={{display:"flex",alignItems:"center",gap:14,padding:"13px 0",borderBottom:`1px solid ${C.border}`,cursor:"pointer"}}>
          <div style={{width:40,height:40,borderRadius:10,background:`${p.color}18`,border:`1px solid ${p.color}33`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0,color:p.color}}>
            {p.icon}
          </div>
          <div style={{flex:1}}>
            <p style={{margin:0,fontSize:15,fontWeight:600,color:C.text}}>{p.label}</p>
            <p style={{margin:0,fontSize:12,color:C.sub}}>{p.desc}</p>
          </div>
          <span style={{color:C.faint,fontSize:16}}>›</span>
        </div>
      ))}

      <div style={{marginTop:20,background:C.bg,borderRadius:12,padding:"12px 14px"}}>
        <p style={{margin:"0 0 6px",fontSize:11,fontWeight:700,letterSpacing:2,color:C.faint,textTransform:"uppercase"}}>Will share</p>
        <p style={{margin:0,fontSize:13,color:C.sub,lineHeight:1.6,whiteSpace:"pre-wrap"}}>{shareText}</p>
      </div>
    </Sheet>
  );
}

// ─── YouTube Player ───────────────────────────────────────────────────────────
function getYouTubeId(url){
  if(!url) return null;
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/\s]{11})/);
  return match ? match[1] : null;
}

function YouTubePlayer({ videoId, title, onClose }){
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.97)",zIndex:500,display:"flex",flexDirection:"column"}}>
      {/* Always visible close button at very top */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 16px",flexShrink:0,background:"rgba(0,0,0,0.8)",zIndex:501}}>
        <p style={{margin:0,fontSize:14,fontWeight:600,color:"#fff",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",paddingRight:12}}>{title}</p>
        <button
          onClick={onClose}
          style={{background:C.brand,border:"none",color:"#fff",borderRadius:10,padding:"10px 20px",cursor:"pointer",fontSize:15,fontWeight:700,flexShrink:0,minWidth:80,zIndex:502}}>
          ✕ Close
        </button>
      </div>
      {/* Video */}
      <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:"8px 0 20px"}}>
        <iframe
          width="100%"
          height="100%"
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
          title={title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{maxWidth:640,maxHeight:"calc(100vh - 120px)",borderRadius:12}}
        />
      </div>
      {/* Extra close button at bottom for easy access */}
      <div style={{padding:"12px 16px",flexShrink:0,background:"rgba(0,0,0,0.8)"}}>
        <button onClick={onClose} style={{width:"100%",background:"rgba(255,255,255,0.1)",border:`1px solid rgba(255,255,255,0.2)`,color:"#fff",borderRadius:10,padding:"12px",cursor:"pointer",fontSize:15,fontWeight:600}}>
          ✕ Close video
        </button>
      </div>
    </div>
  );
}

// ─── YouTube Search ───────────────────────────────────────────────────────────
async function searchYouTube(query){
  const key = process.env.REACT_APP_YOUTUBE_KEY;
  const url = `https://www.googleapis.com/youtube/v3/search?part=snippet&q=${encodeURIComponent(query)}&type=video&maxResults=5&key=${key}`;
  const res = await fetch(url);
  const data = await res.json();
  return (data.items||[]).map(item=>({
    youtube_id: item.id.videoId,
    title: item.snippet.title,
    thumbnail: item.snippet.thumbnails?.medium?.url,
    channel: item.snippet.channelTitle,
  }));
}

function YouTubeSearchSheet({ show, onClose, onAdd }){
  const [mode, setMode] = useState("choice"); // choice | setlist | artist
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState("");
  const [saving, setSaving] = useState(null);
  const [added, setAdded] = useState([]);
  const [autoAdding, setAutoAdding] = useState(false);
  const [autoProgress, setAutoProgress] = useState(0);
  const [autoTotal, setAutoTotal] = useState(0);

  const setlist = show.setlist||[];

  // Search by artist name (general)
  const searchByArtist = async () => {
    setMode("artist");
    setLoading(true);
    const r = await searchYouTube(`${show.artist} live concert`);
    setResults(r);
    setLoading(false);
  };

  // Search one song at a time from setlist
  const searchBySong = async (song) => {
    const r = await searchYouTube(`${show.artist} ${song} live`);
    return r[0] || null;
  };

  // Auto-load ALL songs from setlist
  const autoLoadAll = async () => {
    if(setlist.length === 0){
      setMode("artist");
      searchByArtist();
      return;
    }
    setAutoAdding(true);
    setAutoTotal(setlist.length);
    setAutoProgress(0);

    for(let i = 0; i < setlist.length; i++){
      const song = setlist[i];
      setProgress(`Searching: ${song}...`);
      const item = await searchBySong(song);
      if(item){
        // Save to Supabase directly
        const { data } = await supabase.from("show_media").insert([{
          show_id: show.id.toString(),
          type: "video",
          title: `${show.artist} - ${song}`,
          youtube_id: item.youtube_id,
          url: `https://www.youtube.com/watch?v=${item.youtube_id}`,
        }]).select();
        if(data?.[0]) onAdd(data[0]);
      }
      setAutoProgress(i + 1);
      // Small delay to avoid rate limiting
      await new Promise(r => setTimeout(r, 300));
    }
    setAutoAdding(false);
    setProgress("");
    onClose();
  };

  const handleAdd = async (item) => {
    setSaving(item.youtube_id);
    const { data } = await supabase.from("show_media").insert([{
      show_id: show.id.toString(),
      type: "video",
      title: item.title,
      youtube_id: item.youtube_id,
      url: `https://www.youtube.com/watch?v=${item.youtube_id}`,
    }]).select();
    if(data?.[0]){ onAdd(data[0]); setAdded(p=>[...p, item.youtube_id]); }
    setSaving(null);
  };

  // Auto-loading progress screen
  if(autoAdding) return (
    <Sheet title="Loading videos..." onClose={()=>{}} tall>
      <div style={{textAlign:"center",padding:"40px 0"}}>
        <div style={{fontSize:48,marginBottom:16}}>🎬</div>
        <p style={{margin:"0 0 8px",fontSize:17,fontWeight:700,color:C.text}}>
          {autoProgress} of {autoTotal} songs
        </p>
        <p style={{margin:"0 0 24px",fontSize:14,color:C.sub}}>{progress}</p>
        {/* Progress bar */}
        <div style={{height:6,background:C.border,borderRadius:3,overflow:"hidden",marginBottom:8}}>
          <div style={{height:"100%",background:C.brand,borderRadius:3,width:`${(autoProgress/autoTotal)*100}%`,transition:"width 0.3s"}}/>
        </div>
        <p style={{fontSize:12,color:C.faint}}>Please wait — finding the best live recordings</p>
      </div>
    </Sheet>
  );

  // Choice screen
  if(mode === "choice") return (
    <Sheet title={`Videos for ${show.artist}`} onClose={onClose} tall>
      <p style={{margin:"0 0 20px",fontSize:14,color:C.sub}}>How would you like to find videos?</p>

      {setlist.length > 0 && (
        <div onClick={autoLoadAll} style={{background:`${C.brand}10`,border:`2px solid ${C.brand}`,borderRadius:14,padding:"16px",marginBottom:12,cursor:"pointer"}}>
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:6}}>
            <span style={{fontSize:24}}>⚡</span>
            <p style={{margin:0,fontSize:16,fontWeight:700,color:C.brand}}>Auto-load all songs</p>
          </div>
          <p style={{margin:0,fontSize:13,color:C.sub}}>Automatically find a YouTube video for all {setlist.length} songs in the setlist. One tap — done.</p>
          <div style={{marginTop:10,display:"flex",gap:6,flexWrap:"wrap"}}>
            {setlist.slice(0,4).map(s=><Pill key={s}>{s}</Pill>)}
            {setlist.length>4&&<Pill>+{setlist.length-4} more</Pill>}
          </div>
        </div>
      )}

      <div onClick={()=>{ setMode("artist"); searchByArtist(); }} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:"16px",cursor:"pointer"}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:6}}>
          <span style={{fontSize:24}}>🔍</span>
          <p style={{margin:0,fontSize:16,fontWeight:700,color:C.text}}>Search by artist</p>
        </div>
        <p style={{margin:0,fontSize:13,color:C.sub}}>Browse live concert videos for {show.artist} and pick which ones to add.</p>
      </div>

      {setlist.length === 0 && (
        <p style={{margin:"16px 0 0",fontSize:13,color:C.faint,textAlign:"center"}}>Add a setlist to your show to enable auto-load</p>
      )}
    </Sheet>
  );

  // Artist search results
  return (
    <Sheet title={`Videos for ${show.artist}`} onClose={onClose} tall>
      <p style={{margin:"0 0 16px",fontSize:14,color:C.sub}}>Found on YouTube · tap to add</p>
      {loading&&<p style={{textAlign:"center",color:C.sub,padding:"40px 0"}}>Searching YouTube... 🎬</p>}
      {results.map(item=>(
        <div key={item.youtube_id} style={{marginBottom:14,borderRadius:12,overflow:"hidden",border:`1px solid ${C.border}`}}>
          <img src={item.thumbnail} alt={item.title} style={{width:"100%",display:"block"}}/>
          <div style={{padding:"10px 12px",background:C.card}}>
            <p style={{margin:"0 0 2px",fontSize:13,fontWeight:600,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.title}</p>
            <p style={{margin:"0 0 8px",fontSize:12,color:C.sub}}>{item.channel}</p>
            <button onClick={()=>!added.includes(item.youtube_id)&&handleAdd(item)} disabled={saving===item.youtube_id||added.includes(item.youtube_id)}
              style={{width:"100%",background:added.includes(item.youtube_id)?"#333":C.brand,border:"none",borderRadius:8,padding:"9px",color:"#fff",fontSize:13,fontWeight:700,cursor:added.includes(item.youtube_id)?"default":"pointer"}}>
              {saving===item.youtube_id?"Adding...":added.includes(item.youtube_id)?"✓ Added":"+ Add to show"}
            </button>
          </div>
        </div>
      ))}
      {!loading&&results.length===0&&<p style={{textAlign:"center",color:C.faint,padding:"40px 0"}}>No videos found.</p>}
    </Sheet>
  );
}

function AddVideoSheet({ show, onClose, onAdd }){
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const videoId = getYouTubeId(url);
  const inp = {width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:10,padding:"12px 14px",color:C.text,fontSize:15,outline:"none",fontFamily:"inherit",boxSizing:"border-box"};

  const handleAdd = async () => {
    if(!videoId){ setError("Please paste a valid YouTube link"); return; }
    if(!title.trim()){ setError("Please add a title"); return; }
    setSaving(true);
    // Save to Supabase show_media table
    const { data, error } = await supabase.from("show_media").insert([{
      show_id: show.id.toString(),
      type: "video",
      title: title.trim(),
      youtube_id: videoId,
      url: url.trim(),
    }]).select();
    if(error){ setError("Could not save video. Please try again."); setSaving(false); }
    else { onAdd(data[0]); onClose(); }
  };

  return (
    <Sheet title="Add video" onClose={onClose}>
      <p style={{margin:"0 0 16px",fontSize:14,color:C.sub}}>Paste a YouTube link to a live recording of this show</p>
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <input style={inp} placeholder="YouTube URL (youtube.com/watch?v=...)" value={url} onChange={e=>setUrl(e.target.value)}/>
        {videoId&&(
          <div style={{background:C.bg,borderRadius:10,overflow:"hidden",border:`1px solid ${C.border}`}}>
            <img src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`} alt="thumbnail" style={{width:"100%",display:"block"}}/>
            <p style={{margin:0,padding:"8px 12px",fontSize:12,color:C.green}}>✓ Valid YouTube video found</p>
          </div>
        )}
        <input style={inp} placeholder="Title (e.g. Karma Police live at MSG)" value={title} onChange={e=>setTitle(e.target.value)}/>
        {error&&<p style={{fontSize:13,color:C.red,margin:0}}>{error}</p>}
        <button onClick={handleAdd} disabled={saving||!videoId||!title.trim()}
          style={{background:videoId&&title.trim()?C.brand:"#333",border:"none",borderRadius:12,padding:"14px",color:"#fff",fontSize:15,fontWeight:700,cursor:"pointer"}}>
          {saving?"Saving...":"Add video"}
        </button>
      </div>
    </Sheet>
  );
}

// ─── Media Player ─────────────────────────────────────────────────────────────
function MediaPlayer({ show, mediaItems, onUpgrade, onMediaAdded }){
  const isPro = useIsPro();
  const [playing, setPlaying] = useState(null);
  const [sharing, setSharing] = useState(null);
  const [addingVideo, setAddingVideo] = useState(false);
  const [searching, setSearching] = useState(false);
  const [items, setItems] = useState([]);

  // Sync with mediaItems prop whenever it changes
  useEffect(()=>{
    if(mediaItems && mediaItems.length > 0){
      setItems(mediaItems);
    }
  },[mediaItems]);

  const handleAdd = (item) => {
    setItems(p=>[...p, item]);
    if(onMediaAdded) onMediaAdded(item);
  };

  const handlePlay = (item) => {
    if(!isPro){ onUpgrade(); return; }
    setPlaying(item);
  };

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <p style={{margin:0,fontSize:11,fontWeight:700,letterSpacing:2,color:C.faint,textTransform:"uppercase"}}>Videos & Recordings</p>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {!isPro&&<Pill color={C.brand}>PRO</Pill>}
          {isPro&&<button onClick={()=>setSearching(true)} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:8,padding:"5px 12px",color:C.sub,fontSize:12,cursor:"pointer"}}>
            🔍 Search
          </button>}
          <button onClick={()=>isPro?setAddingVideo(true):onUpgrade()} style={{background:C.brand,border:"none",borderRadius:8,padding:"5px 12px",color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer"}}>
            + Add
          </button>
        </div>
      </div>

      {items.length===0&&(
        <div style={{background:C.bg,borderRadius:12,padding:"20px",textAlign:"center",border:`1px solid ${C.border}`}}>
          <p style={{margin:"0 0 4px",fontSize:15,color:C.sub}}>No videos yet</p>
          <p style={{margin:"0 0 12px",fontSize:13,color:C.faint}}>Search YouTube automatically or paste a link</p>
          {isPro
            ?<button onClick={()=>setSearching(true)} style={{background:C.brand,border:"none",borderRadius:8,padding:"9px 20px",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer"}}>🔍 Search YouTube</button>
            :<button onClick={onUpgrade} style={{background:C.brand,border:"none",borderRadius:8,padding:"9px 20px",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer"}}>🔒 Unlock with Pro</button>
          }
        </div>
      )}

      {items.map((item,i)=>{
        const videoId = item.youtube_id || getYouTubeId(item.url);
        return (
          <div key={i} style={{marginBottom:12,borderRadius:12,overflow:"hidden",border:`1px solid ${C.border}`}}>
            {/* Thumbnail */}
            {videoId&&(
              <div style={{position:"relative",cursor:"pointer"}} onClick={()=>handlePlay(item)}>
                <img src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`} alt={item.title} style={{width:"100%",display:"block"}}/>
                <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,0.3)"}}>
                  {isPro
                    ?<div style={{width:52,height:52,borderRadius:"50%",background:C.brand,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>▶</div>
                    :<div style={{width:52,height:52,borderRadius:"50%",background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>🔒</div>
                  }
                </div>
              </div>
            )}
            {/* Info row */}
            <div style={{padding:"10px 12px",background:C.card,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{flex:1,minWidth:0}}>
                <p style={{margin:0,fontSize:14,fontWeight:600,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.title}</p>
                <p style={{margin:0,fontSize:12,color:C.sub}}>YouTube · {isPro?"Tap to play":"Pro only"}</p>
              </div>
              {isPro&&(
                <button onClick={()=>setSharing(item)} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:8,padding:"5px 10px",color:C.sub,fontSize:12,cursor:"pointer",flexShrink:0,marginLeft:8}}>
                  ↑ Share
                </button>
              )}
            </div>
          </div>
        );
      })}

      {playing&&<YouTubePlayer videoId={playing.youtube_id||getYouTubeId(playing.url)} title={playing.title} onClose={()=>setPlaying(null)}/>}
      {sharing&&<ShareMediaSheet item={sharing} show={show} onClose={()=>setSharing(null)}/>}
      {addingVideo&&<AddVideoSheet show={show} onClose={()=>setAddingVideo(false)} onAdd={handleAdd}/>}
      {searching&&<YouTubeSearchSheet show={show} onClose={()=>setSearching(false)} onAdd={handleAdd}/>}
    </div>
  );
}

// ─── Setlist.fm API ───────────────────────────────────────────────────────────
async function fetchSetlistFm(artist, date){
  try {
    const res = await fetch(`https://www.wazat.app/api/setlist?artist=${encodeURIComponent(artist)}`);
    const data = await res.json();
    const setlists = data?.setlist || [];
    // Find setlist matching date if provided, otherwise use most recent with songs
    const match = date
      ? setlists.find(s => {
          const parts = s.eventDate.split("-");
          const d = `${parts[2]}-${parts[1]}-${parts[0]}`;
          return d === date;
        })
      : setlists.find(s => (s.sets?.set||[]).flatMap(x=>x.song||[]).length > 0);
    if(!match) return [];
    return (match.sets?.set||[]).flatMap(s=>s.song||[]).map(s=>s.name).filter(Boolean);
  } catch(e){
    return [];
  }
}

// ─── Concert Setlist (for upcoming shows) ─────────────────────────────────────
function ConcertSetlist({ concert, onUpgrade }){
  const isPro = useIsPro();
  const [songs, setSongs] = useState(concert.concertSetlist||[]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("loading");
  const [previewSong, setPreviewSong] = useState(null);

  useEffect(()=>{
    setLoading(true);
    // Clean artist name — remove tour names, "presents", "starring" etc.
    const cleanArtist = concert.artist
      .replace(/presents?:?.*/i, "")
      .replace(/starring.*/i, "")
      .replace(/\s*-\s*.*/i, "")
      .replace(/\s*:\s*.*/i, "")
      .trim();
    fetchSetlistFm(cleanArtist, null)
      .then(result=>{
        if(result.length>0){ setSongs(result); setSource("setlistfm"); }
        else { setSongs(concert.concertSetlist||[]); setSource("sample"); }
        setLoading(false);
      })
      .catch(()=>{ setSongs(concert.concertSetlist||[]); setSource("sample"); setLoading(false); });
  },[concert.artist]);

  const preview = songs.slice(0,3);
  const rest = songs.slice(3);

  if(loading) return <p style={{textAlign:"center",color:C.sub,padding:"20px 0",fontSize:14}}>Loading setlist... 🎵</p>;
  if(songs.length===0) return <p style={{textAlign:"center",color:C.faint,padding:"20px 0",fontSize:14}}>No setlist found yet</p>;

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <p style={{margin:0,fontSize:11,fontWeight:700,letterSpacing:2,color:C.faint,textTransform:"uppercase"}}>
          Expected Setlist {!isPro&&<span style={{color:C.brand,fontSize:10}}>· PRO</span>}
        </p>
        <p style={{margin:0,fontSize:11,color:source==="setlistfm"?C.green:C.faint}}>
          {source==="setlistfm"?"● setlist.fm":"● estimated"}
        </p>
      </div>

      {preview.map((s,i)=>(
        <div key={i} onClick={()=>setPreviewSong(s)} style={{display:"flex",gap:12,padding:"8px 0",borderBottom:`1px solid ${C.border}`,cursor:"pointer",alignItems:"center"}}>
          <span style={{color:C.faint,fontSize:12,width:20,flexShrink:0,textAlign:"right"}}>{i+1}</span>
          <span style={{fontSize:15,color:C.text,flex:1}}>{s}</span>
          <span style={{fontSize:12,color:C.brand}}>▶</span>
        </div>
      ))}

      {isPro ? (
        rest.map((s,i)=>(
          <div key={i} onClick={()=>setPreviewSong(s)} style={{display:"flex",gap:12,padding:"8px 0",borderBottom:`1px solid ${C.border}`,cursor:"pointer",alignItems:"center"}}>
            <span style={{color:C.faint,fontSize:12,width:20,flexShrink:0,textAlign:"right"}}>{i+4}</span>
            <span style={{fontSize:15,color:C.text,flex:1}}>{s}</span>
            <span style={{fontSize:12,color:C.brand}}>▶</span>
          </div>
        ))
      ):(
        <div style={{position:"relative"}}>
          {rest.slice(0,2).map((s,i)=>(
            <div key={i} style={{display:"flex",gap:12,padding:"8px 0",borderBottom:`1px solid ${C.border}`,filter:"blur(4px)",userSelect:"none",pointerEvents:"none"}}>
              <span style={{color:C.faint,fontSize:12,width:20,flexShrink:0,textAlign:"right"}}>{i+4}</span>
              <span style={{fontSize:15,color:C.text}}>{s}</span>
            </div>
          ))}
          <div style={{background:`linear-gradient(to bottom, transparent, ${C.card})`,position:"absolute",inset:0,display:"flex",alignItems:"flex-end",justifyContent:"center",paddingBottom:4}}>
            <button onClick={onUpgrade} style={{background:C.brand,border:"none",borderRadius:10,padding:"8px 20px",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer"}}>
              Unlock full setlist — Pro
            </button>
          </div>
          <div style={{height:60}}/>
        </div>
      )}

      {previewSong&&<SongPreviewPlayer song={previewSong} artist={concert.artist} onClose={()=>setPreviewSong(null)}/>}
    </div>
  );
}

// ─── Monetization Sheet ───────────────────────────────────────────────────────
function MonetizationSheet({ onClose }){
  const isPro = useIsPro();
  const [tab, setTab] = useState("plans");
  const [loading, setLoading] = useState(null);

  const PLANS = [
    { id:"free", label:"Free", price:"$0", color:C.sub,
      features:["Log up to 20 shows","Basic setlist tracking","3 friends","Home feed & notifications","Full ticket access & price alerts","Venue seat maps","WhatsApp invites"] },
    { id:"pro", label:"Pro", price:"$4.99/mo", color:C.brand, popular:true,
      features:["Everything in Free","Unlimited show logging","Full expected setlists","Music & video player","Share media to WhatsApp, TikTok & Instagram","Grail tracker & predictions","Unlimited friends","Ad-free"] },
    { id:"superfan", label:"Superfan", price:"$9.99/mo", color:C.gold,
      features:["Everything in Pro","AI setlist predictions","Early ticket access","Custom profile & badges","Export history","Concert AI companion"] },
  ];

  const REVENUE = [
    { icon:"👑", title:"Subscriptions", desc:"Pro at $4.99/mo and Superfan at $9.99/mo. Est. 8–12% conversion.", est:"$2–4 / user / mo", color:C.brand },
    { icon:"🎟", title:"Ticket Affiliates", desc:"3–6% on every ticket sold via StubHub, SeatGeek, Ticketmaster.", est:"$4–15 / ticket", color:C.green },
    { icon:"🎸", title:"Artist Sponsorships", desc:"Promoted listings, presale access, sponsored setlist recaps.", est:"$5–50K / campaign", color:C.gold },
    { icon:"📦", title:"Merch Affiliate", desc:"Surface official merch after each show. 8–12% per sale.", est:"$1–8 / sale", color:C.red },
    { icon:"📊", title:"Fan Data (B2B)", desc:"Anonymized attendance & taste data sold to labels and promoters.", est:"$50K–500K / yr", color:"#6499e8" },
    { icon:"🌟", title:"Verified Artists", desc:"Artists pay for verified profiles, setlist editing, fan messaging.", est:"$99–499 / mo", color:C.brand },
  ];

  const handleUpgrade = async (tier) => {
    setLoading(tier);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const res = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tier, user_id: user.id, email: user.email })
      });
      const data = await res.json();
      if(data.url) window.location.href = data.url;
      else alert("Could not start checkout. Please try again.");
    } catch(err) {
      alert("Error: " + err.message);
    }
    setLoading(null);
  };

  return (
    <Sheet title="Wazat Plans" onClose={onClose} tall>
      {/* Tab toggle */}
      <div style={{display:"flex",background:C.bg,borderRadius:10,padding:3,marginBottom:20,gap:3}}>
        {[{k:"plans",l:"💎 Plans"},{k:"revenue",l:"💰 Revenue"}].map(t=>(
          <button key={t.k} onClick={()=>setTab(t.k)} style={{flex:1,background:tab===t.k?C.card:"none",border:tab===t.k?`1px solid ${C.border}`:"1px solid transparent",borderRadius:8,padding:"9px",color:tab===t.k?C.text:C.sub,fontSize:14,fontWeight:tab===t.k?600:400,cursor:"pointer"}}>
            {t.l}
          </button>
        ))}
      </div>

      {tab==="plans"&&<>
        {PLANS.map(plan=>(
          <div key={plan.id} style={{border:`1px solid ${plan.popular?`${C.brand}55`:C.border}`,borderRadius:14,padding:"16px",marginBottom:12,background:plan.popular?`${C.brand}08`:"none",position:"relative"}}>
            {plan.popular&&<div style={{position:"absolute",top:-1,right:16,background:C.brand,borderRadius:"0 0 8px 8px",padding:"2px 10px",fontSize:11,fontWeight:700,color:"#fff"}}>POPULAR</div>}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
              <span style={{fontSize:18,fontWeight:700,color:plan.color}}>{plan.label}</span>
              <span style={{fontSize:16,fontWeight:700,color:C.text}}>{plan.price}</span>
            </div>
            {plan.features.map(f=>(
              <div key={f} style={{display:"flex",gap:10,marginBottom:6,alignItems:"flex-start"}}>
                <span style={{color:plan.color,fontSize:13,flexShrink:0,marginTop:1}}>✓</span>
                <span style={{fontSize:14,color:C.sub}}>{f}</span>
              </div>
            ))}
            {plan.id!=="free"&&(
              <button
                onClick={()=>handleUpgrade(plan.id)}
                disabled={loading===plan.id}
                style={{width:"100%",marginTop:14,background:plan.id==="pro"?C.brand:`linear-gradient(135deg,#b8862a,${C.gold})`,border:"none",borderRadius:10,padding:"12px",color:plan.id==="pro"?"#fff":"#0f0f0f",fontSize:15,fontWeight:700,cursor:"pointer"}}>
                {loading===plan.id?"Loading...":(isPro&&plan.id==="pro"?"✓ Current plan":`Start ${plan.label} — ${plan.price}`)}
              </button>
            )}
          </div>
        ))}
        <p style={{textAlign:"center",fontSize:12,color:C.faint,marginTop:8}}>Secure payments via Stripe · Cancel anytime</p>
      </>}

      {tab==="revenue"&&<>
        <p style={{margin:"0 0 16px",fontSize:14,color:C.sub,lineHeight:1.6}}>Wazat has 6 revenue streams that keep the core app free while rewarding power users.</p>
        {REVENUE.map(r=>(
          <div key={r.title} style={{display:"flex",gap:14,padding:"14px 0",borderBottom:`1px solid ${C.border}`}}>
            <div style={{width:38,height:38,borderRadius:10,background:`${r.color}18`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:19,flexShrink:0}}>{r.icon}</div>
            <div style={{flex:1}}>
              <p style={{margin:"0 0 2px",fontSize:15,fontWeight:600,color:C.text}}>{r.title}</p>
              <p style={{margin:"0 0 6px",fontSize:13,color:C.sub,lineHeight:1.5}}>{r.desc}</p>
              <Pill color={r.color}>Est: {r.est}</Pill>
            </div>
          </div>
        ))}
        <div style={{marginTop:16,background:C.bg,borderRadius:12,padding:"14px 16px",border:`1px solid ${C.green}22`}}>
          <p style={{margin:"0 0 6px",fontSize:14,fontWeight:700,color:C.green}}>📈 Unit Economics</p>
          <p style={{margin:0,fontSize:13,color:C.sub,lineHeight:1.6}}>At 100K active users with 10% Pro conversion: <strong style={{color:C.text}}>~$50K MRR</strong> from subscriptions. Add ticket affiliate at 2 tickets/user/month: <strong style={{color:C.text}}>+$80–160K/mo</strong>.</p>
        </div>
      </>}
    </Sheet>
  );
}

// ─── Song Preview Player ──────────────────────────────────────────────────────
function SongPreviewPlayer({ song, artist, onClose }){
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(()=>{
    // Search iTunes via API route to avoid CORS
    fetch(`https://www.wazat.app/api/preview?artist=${encodeURIComponent(artist)}&song=${encodeURIComponent(song)}`)
      .then(r=>r.json())
      .then(data=>{
        const track = data.results?.[0];
        if(track) setPreview(track);
        setLoading(false);
      })
  .catch(err=>{ console.error("Wazat concerts error:", err); setLoading(false); });
  },[song, artist]);

  const togglePlay = () => {
    if(!audioRef.current) return;
    if(playing){ audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };

  const spotifyUrl = `https://open.spotify.com/search/${encodeURIComponent(`${artist} ${song}`)}`;
  const appleMusicUrl = `https://music.apple.com/search?term=${encodeURIComponent(`${artist} ${song}`)}`;

  return (
    <Sheet title={song} onClose={onClose}>
      <p style={{margin:"0 0 20px",fontSize:14,color:C.sub}}>{artist}</p>

      {loading&&<p style={{textAlign:"center",color:C.sub,padding:"20px 0"}}>Finding preview... 🎵</p>}

      {!loading&&preview&&(
        <div style={{background:C.bg,borderRadius:14,padding:"16px",marginBottom:16,border:`1px solid ${C.border}`}}>
          {/* Album art */}
          <div style={{display:"flex",gap:14,alignItems:"center",marginBottom:14}}>
            <img src={preview.artworkUrl100} alt={preview.trackName} style={{width:64,height:64,borderRadius:10}}/>
            <div style={{flex:1,minWidth:0}}>
              <p style={{margin:0,fontSize:15,fontWeight:700,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{preview.trackName}</p>
              <p style={{margin:"2px 0 0",fontSize:13,color:C.sub}}>{preview.artistName}</p>
              <p style={{margin:"2px 0 0",fontSize:12,color:C.faint}}>{preview.collectionName}</p>
            </div>
          </div>

          {/* 30-second preview player */}
          {preview.previewUrl&&<>
            <audio ref={audioRef} src={preview.previewUrl} onEnded={()=>setPlaying(false)}/>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:8}}>
              <button onClick={togglePlay} style={{width:48,height:48,borderRadius:"50%",background:C.brand,border:"none",color:"#fff",fontSize:20,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                {playing?"⏸":"▶"}
              </button>
              <div style={{flex:1}}>
                <p style={{margin:"0 0 2px",fontSize:13,color:C.text,fontWeight:600}}>{playing?"Playing preview...":"30-second preview"}</p>
                <p style={{margin:0,fontSize:12,color:C.faint}}>iTunes · tap to {playing?"pause":"play"}</p>
              </div>
            </div>
          </>}
          {!preview.previewUrl&&<p style={{fontSize:13,color:C.faint,margin:0}}>No preview available for this track</p>}
        </div>
      )}

      {!loading&&!preview&&(
        <div style={{textAlign:"center",padding:"20px 0",marginBottom:16}}>
          <p style={{color:C.faint,fontSize:14}}>No preview found for this song</p>
        </div>
      )}

      {/* Deep links */}
      <p style={{margin:"0 0 10px",fontSize:11,fontWeight:700,letterSpacing:2,color:C.faint,textTransform:"uppercase"}}>Open full song in</p>
      <div style={{display:"flex",gap:10}}>
        <a href={spotifyUrl} target="_blank" rel="noreferrer" style={{flex:1,background:"#1DB954",border:"none",borderRadius:12,padding:"13px",color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",textAlign:"center",textDecoration:"none",display:"block"}}>
          ♫ Spotify
        </a>
        <a href={appleMusicUrl} target="_blank" rel="noreferrer" style={{flex:1,background:"#fc3c44",border:"none",borderRadius:12,padding:"13px",color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",textAlign:"center",textDecoration:"none",display:"block"}}>
          ♪ Apple Music
        </a>
      </div>
    </Sheet>
  );
}

// ─── Show Detail Sheet ────────────────────────────────────────────────────────
function ShowSheet({ show, onClose, onOpenMonetize }){
  const [mediaItems, setMediaItems] = useState([]);
  const [realSetlist, setRealSetlist] = useState(show.setlist||[]);
  const [setlistSource, setSetlistSource] = useState("logged");
  const [previewSong, setPreviewSong] = useState(null);

  useEffect(()=>{
    if(!show.id) return;
    // Load media
    supabase.from("show_media").select("*").eq("show_id", show.id.toString()).order("created_at", {ascending:false})
      .then(({data})=>{ if(data && data.length>0) setMediaItems(data); });
    // Auto-fetch real setlist from Setlist.fm if user's setlist is empty
    if(!show.setlist||show.setlist.length===0){
      const cleanArtist = show.artist
        .replace(/presents?:?.*/i, "")
        .replace(/starring.*/i, "")
        .replace(/\s*-\s*.*/i, "")
        .trim();
      fetchSetlistFm(cleanArtist, show.date)
        .then(songs=>{ if(songs.length>0){ setRealSetlist(songs); setSetlistSource("setlistfm"); } });
    }
  },[show.id]);

  return (
    <Sheet title={show.artist} onClose={onClose} tall>
      {/* Show photo if available */}
      {show.photo_url&&(
        <div style={{marginBottom:16,borderRadius:12,overflow:"hidden"}}>
          <img src={show.photo_url} alt={show.artist} style={{width:"100%",maxHeight:220,objectFit:"cover",display:"block"}}/>
        </div>
      )}
      <p style={{margin:"0 0 4px",fontSize:14,color:C.sub}}>{show.venue} · {show.city}</p>
      <p style={{margin:"0 0 12px",fontSize:13,color:C.faint}}>{fmtDate(show.date)}</p>
      <Stars n={show.rating}/>

      {show.notes&&<p style={{margin:"14px 0",fontSize:14,color:C.sub,lineHeight:1.6,fontStyle:"italic",borderLeft:`3px solid ${C.brand}`,paddingLeft:12}}>"{show.notes}"</p>}

      {/* Setlist */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",margin:"16px 0 8px"}}>
        <p style={{margin:0,fontSize:11,fontWeight:700,letterSpacing:2,color:C.faint,textTransform:"uppercase"}}>Setlist</p>
        {setlistSource==="setlistfm"&&<p style={{margin:0,fontSize:11,color:C.green}}>● setlist.fm</p>}
      </div>
      {realSetlist.length===0
        ?<p style={{fontSize:14,color:C.faint,padding:"10px 0"}}>No setlist added yet</p>
        :realSetlist.map((s,i)=>(
          <div key={i} onClick={()=>setPreviewSong(s)} style={{display:"flex",gap:12,padding:"8px 0",borderBottom:`1px solid ${C.border}`,cursor:"pointer",alignItems:"center"}}>
            <span style={{color:C.faint,fontSize:12,width:20,flexShrink:0,textAlign:"right"}}>{i+1}</span>
            <span style={{fontSize:15,color:C.text,flex:1}}>{s}</span>
            <span style={{fontSize:12,color:C.brand}}>▶</span>
          </div>
        ))
      }

      {previewSong&&<SongPreviewPlayer song={previewSong} artist={show.artist} onClose={()=>setPreviewSong(null)}/>}

      {show.grails?.length>0&&<>
        <p style={{margin:"16px 0 8px",fontSize:11,fontWeight:700,letterSpacing:2,color:C.faint,textTransform:"uppercase"}}>Grails not played</p>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>{show.grails.map(g=><Pill key={g} color={C.gold}>✦ {g}</Pill>)}</div>
      </>}

      {/* Media player */}
      <div style={{height:1,background:C.border,margin:"20px 0"}}/>
      <MediaPlayer
        show={show}
        mediaItems={mediaItems}
        onUpgrade={onOpenMonetize}
        onMediaAdded={item=>setMediaItems(p=>[item,...p])}
      />
    </Sheet>
  );
}

// ─── Concert Detail Sheet ─────────────────────────────────────────────────────
function ConcertSheet({ show, onClose, onOpenMonetize, onGoing, goingIds }){
  const [saved, setSaved] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [addingGoing, setAddingGoing] = useState(false);
  const isGoing = goingIds?.includes(show.id);
  const trendColor = show.trend==="rising"?C.red:show.trend==="falling"?C.green:C.gold;
  const trendIcon  = show.trend==="rising"?"↑":show.trend==="falling"?"↓":"→";

  return (
    <Sheet title={show.artist} onClose={onClose} tall>
      <ArtistBanner name={show.artistName||show.artist} image={show.image}/>
      <p style={{margin:"0 0 4px",fontSize:14,color:C.sub}}>{show.venue}</p>
      <p style={{margin:"0 0 16px",fontSize:13,color:C.faint}}>{fmtDate(show.date)} · {daysUntil(show.date)<0?"Already happened":daysUntil(show.date)===0?"Today":`${daysUntil(show.date)} days away`}</p>

      {/* Ticket price */}
      <div style={{background:C.bg,borderRadius:12,padding:"14px 16px",marginBottom:14}}>
        {show.low && show.high ? (
          <>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:10}}>
              <div>
                <p style={{margin:0,fontSize:11,color:C.faint,textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>Ticket range</p>
                <p style={{margin:0,fontSize:22,fontWeight:700,color:C.text}}>${show.low} <span style={{fontSize:15,color:C.sub}}>– ${show.high}</span></p>
              </div>
              {show.avg&&<div style={{textAlign:"right"}}>
                <p style={{margin:0,fontSize:11,color:C.faint,marginBottom:4}}>avg</p>
                <p style={{margin:0,fontSize:18,fontWeight:700,color:C.brand}}>${show.avg}</p>
              </div>}
            </div>
            {/* Price bar */}
            <div style={{position:"relative",height:6,borderRadius:3,background:C.border,marginBottom:8}}>
              <div style={{position:"absolute",height:"100%",borderRadius:3,background:`linear-gradient(90deg,${C.green},${C.gold},${C.red})`,opacity:0.5,width:"100%"}}/>
              {show.avg&&show.low!==show.high&&<div style={{position:"absolute",top:"50%",left:`${Math.min(95,Math.max(5,((show.avg-show.low)/(show.high-show.low))*100))}%`,transform:"translate(-50%,-50%)",width:12,height:12,borderRadius:"50%",background:C.brand,border:`2px solid ${C.card}`}}/>}
            </div>
            {/* Min/Max labels */}
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
              <span style={{fontSize:11,color:C.faint}}>${show.low} min</span>
              <span style={{fontSize:11,color:C.faint}}>${show.high} max</span>
            </div>
          </>
        ) : (
          <div style={{textAlign:"center",padding:"8px 0"}}>
            <p style={{margin:0,fontSize:15,color:C.sub}}>Ticket prices available on Ticketmaster</p>
            <p style={{margin:"4px 0 0",fontSize:12,color:C.faint}}>Tap Get Tickets to see current prices</p>
          </div>
        )}
        <p style={{margin:"8px 0 0",fontSize:12,color:trendColor,fontWeight:600}}>
          {trendIcon} {show.trend==="rising"?`Up ${show.pct}% this week`:show.trend==="falling"?`Down ${Math.abs(show.pct)}% — good time to buy`:"Prices vary — check Ticketmaster"}
        </p>
      </div>

      {show.grailChance>0&&<div style={{background:`${C.gold}12`,border:`1px solid ${C.gold}33`,borderRadius:10,padding:"10px 14px",marginBottom:14,display:"flex",gap:10,alignItems:"center"}}>
        <span style={{fontSize:18}}>✦</span>
        <div>
          <p style={{margin:0,fontSize:14,fontWeight:600,color:C.gold}}>{show.grailChance}% grail chance</p>
          <p style={{margin:0,fontSize:12,color:C.sub}}>Based on recent setlists</p>
        </div>
      </div>}

      {show.friends.length>0&&<p style={{margin:"0 0 14px",fontSize:13,color:C.sub}}>👥 {show.friends.join(" & ")} {show.friends.length===1?"is":"are"} going</p>}

      {/* I'm going button */}
      <button
        onClick={()=>{ if(!isGoing){ onGoing(show); setAddingGoing(true); setTimeout(()=>setAddingGoing(false),2000); } }}
        style={{width:"100%",background:isGoing?`${C.green}22`:"none",border:`2px solid ${isGoing?C.green:C.border}`,borderRadius:12,padding:"13px",color:isGoing?C.green:C.sub,fontSize:15,fontWeight:700,cursor:isGoing?"default":"pointer",marginBottom:10,transition:"all 0.2s"}}>
        {addingGoing?"Added to My Shows! 🎉":isGoing?"🎟 You're going!":"🎟 I'm going!"}
      </button>

      {/* Get tickets + actions */}
      <button onClick={()=>show.url?window.open(show.url,"_blank"):null} style={{width:"100%",background:C.brand,border:"none",borderRadius:12,padding:"14px",color:"#fff",fontSize:16,fontWeight:700,cursor:"pointer",marginBottom:10}}>
        {show.low?`Get Tickets · from $${show.low}`:"Get Tickets"}
      </button>
      <div style={{display:"flex",gap:8,marginBottom:10}}>
        <button onClick={()=>setSaved(s=>!s)} style={{flex:1,background:"none",border:`1px solid ${saved?C.gold:C.border}`,borderRadius:12,padding:"12px",color:saved?C.gold:C.sub,fontSize:14,fontWeight:600,cursor:"pointer"}}>
          {saved?"✦ Saved":"✦ Save"}
        </button>
        <button onClick={()=>setInviting(true)} style={{flex:1,background:"none",border:`1px solid ${C.wa}44`,borderRadius:12,padding:"12px",color:C.wa,fontSize:14,fontWeight:600,cursor:"pointer"}}>
          💬 Invite
        </button>
        <button onClick={()=>setShowMap(true)} style={{flex:1,background:"none",border:`1px solid ${C.border}`,borderRadius:12,padding:"12px",color:C.sub,fontSize:13,cursor:"pointer"}}>
          🗺 Map
        </button>
      </div>

      {/* Google Calendar */}
      <button style={{width:"100%",background:"none",border:`1px solid ${C.border}`,borderRadius:12,padding:"12px",color:C.sub,fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginBottom:10}}
        onClick={()=>{
          const start = show.date?.replace(/-/g,"") + "T190000";
          const end = show.date?.replace(/-/g,"") + "T230000";
          const title = encodeURIComponent(`${show.artist} at ${show.venue}`);
          const details = encodeURIComponent(`Discovered via Encore — encore-d55e.vercel.app`);
          const location = encodeURIComponent((show.venue||"") + ", " + (show.city||"Toronto, ON"));
          window.open(`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&details=${details}&location=${location}`, "_blank");
        }}>
        📅 Add to Google Calendar
      </button>

      {/* Listen */}
      <div style={{display:"flex",gap:8,marginBottom:14}}>
        <button onClick={()=>window.open(`https://open.spotify.com/search/${encodeURIComponent(show.artistName||show.artist)}`,"_blank")} style={{flex:1,background:"none",border:"1px solid #1DB95466",borderRadius:12,padding:"12px",color:"#1DB954",fontSize:14,fontWeight:600,cursor:"pointer"}}>
          🎧 Listen on Spotify
        </button>
        <button onClick={()=>window.open(`https://music.apple.com/search?term=${encodeURIComponent(show.artistName||show.artist)}`,"_blank")} style={{flex:1,background:"none",border:`1px solid ${C.border}`,borderRadius:12,padding:"12px",color:C.sub,fontSize:14,fontWeight:600,cursor:"pointer"}}>
          🎵 Apple Music
        </button>
      </div>
      
      {/* Expected setlist */}
      {show.concertSetlist&&show.concertSetlist.length>0&&<>
        <div style={{height:1,background:C.border,margin:"4px 0 16px"}}/>
        <ConcertSetlist concert={show} onUpgrade={onOpenMonetize}/>
      </>}

      {inviting&&<WhatsAppSheet concert={show} onClose={()=>setInviting(false)}/>}
      {showMap&&<VenueMapSheet concert={show} onClose={()=>setShowMap(false)}/>}
    </Sheet>
  );
}

// ─── WhatsApp Sheet ───────────────────────────────────────────────────────────
function WhatsAppSheet({ concert, onClose }){
  const msg = concert
    ?`Hey! Going to see ${concert.artist} at ${concert.venue} on ${fmtShort(concert.date)}${concert.low?`. Tickets from $${concert.low}`:""}. Want to come? 🎤\n\nencore-d55e.vercel.app`
    :`Come join me on Wazat — the live music journal app for real fans 🎤\n\nwazat.app`;

  const handleShare = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
    onClose();
  };

  return (
    <Sheet title="Invite via WhatsApp" onClose={onClose}>
      <p style={{margin:"0 0 12px",fontSize:14,color:C.sub}}>Message preview</p>
      <div style={{background:C.bg,borderRadius:12,padding:"14px",marginBottom:20,fontSize:14,color:C.text,lineHeight:1.7,whiteSpace:"pre-wrap"}}>{msg}</div>
      <p style={{margin:"0 0 16px",fontSize:13,color:C.faint,textAlign:"center"}}>Opens WhatsApp where you can pick who to send it to</p>
      <button onClick={handleShare} style={{width:"100%",background:C.wa,border:"none",borderRadius:12,padding:"14px",color:"#fff",fontSize:15,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
        💬 Open WhatsApp
      </button>
    </Sheet>
  );
}

// ─── Venue Map Sheet ──────────────────────────────────────────────────────────
function VenueMapSheet({ concert, onClose }){
  const place = [concert.venue, concert.city].filter(Boolean).join(", ");
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place)}`;
  return (
    <Sheet title={concert.venue} onClose={onClose}>
      <p style={{margin:"0 0 16px",fontSize:13,color:C.faint}}>{concert.artist} · {fmtDate(concert.date)}</p>
      {concert.seatmap&&<div style={{background:C.bg,borderRadius:12,padding:10,marginBottom:14}}>
        <p style={{margin:"0 0 8px",fontSize:11,color:C.faint,textTransform:"uppercase",letterSpacing:1}}>Seat map</p>
        <img src={concert.seatmap} alt="Seat map" style={{display:"block",width:"100%",borderRadius:8,background:"#fff"}}/>
      </div>}
      {!concert.seatmap&&<div style={{background:C.bg,borderRadius:12,padding:"14px 16px",marginBottom:14}}>
        <p style={{margin:0,fontSize:14,color:C.sub,lineHeight:1.5}}>The seat map for this show isn't available here. Ticketmaster shows the sections and prices on the ticket page.</p>
      </div>}
      {concert.low&&<div style={{background:C.bg,borderRadius:12,padding:"14px 16px",marginBottom:14}}>
        <p style={{margin:0,fontSize:11,color:C.faint,textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>Ticket range</p>
        <p style={{margin:0,fontSize:22,fontWeight:700,color:C.text}}>${concert.low}{concert.high&&concert.high!==concert.low?<span style={{fontSize:15,color:C.sub}}> – ${concert.high}</span>:null}</p>
      </div>}
      <button onClick={()=>window.open(mapsUrl,"_blank")} style={{width:"100%",background:"none",border:`1px solid ${C.border}`,borderRadius:12,padding:"14px",color:C.text,fontSize:15,fontWeight:700,cursor:"pointer",marginBottom:10}}>
        🗺 Open in Google Maps
      </button>
      <button onClick={()=>concert.url?window.open(concert.url,"_blank"):null} style={{width:"100%",background:C.brand,border:"none",borderRadius:12,padding:"14px",color:"#fff",fontSize:15,fontWeight:700,cursor:"pointer"}}>
      See seats & tickets on Ticketmaster
      </button>
    </Sheet>
  );
}

// ─── Add Show Sheet ───────────────────────────────────────────────────────────
function AddShowSheet({ onClose, onAdd, userId }){
  const [f, setF] = useState({artist:"",venue:"",date:"",rating:0,notes:"",setlist:"",grails:""});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [photo, setPhoto] = useState(null);
  const fileRef = useRef(null);
  const set = (k,v) => setF(p=>({...p,[k]:v}));
  const inp = {width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:10,padding:"12px 14px",color:C.text,fontSize:15,outline:"none",fontFamily:"inherit",boxSizing:"border-box"};
  const valid = f.artist.trim()&&f.date;

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if(!file) return;
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if(!valid) return;
    setSaving(true);
    setError("");

    // Upload photo to Supabase Storage if one was taken
    let photo_url = null;
    if(photo){
      try {
        const blob = await fetch(photo).then(r=>r.blob());
        const ext = blob.type.split("/")[1] || "jpg";
        const filename = `${userId}/${Date.now()}.${ext}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from("show-photos")
          .upload(filename, blob, { contentType: blob.type, upsert: false });
        if(!uploadError && uploadData){
          const { data: urlData } = supabase.storage.from("show-photos").getPublicUrl(filename);
          photo_url = urlData?.publicUrl;
        }
      } catch(err) {
        console.error("Photo upload failed:", err);
      }
    }

    const newShow = {
      user_id: userId,
      artist: f.artist.trim(),
      venue: f.venue.trim(),
      city: "Toronto, ON",
      show_date: f.date,
      rating: f.rating || 3,
      notes: f.notes.trim(),
      setlist: f.setlist.split("\n").map(s=>s.trim()).filter(Boolean).join("|"),
      grails: f.grails.split("\n").map(s=>s.trim()).filter(Boolean).join("|"),
      photo_url,
    };

    const { data, error } = await supabase.from("shows").insert([newShow]).select();
    if(error){
      setError("Could not save show. Please try again.");
      setSaving(false);
    } else {
      const saved = data[0];
      onAdd({
        id: saved.id,
        artist: saved.artist,
        venue: saved.venue,
        city: saved.city,
        date: saved.show_date,
        rating: saved.rating,
        notes: saved.notes,
        setlist: saved.setlist ? saved.setlist.split("|") : [],
        grails: saved.grails ? saved.grails.split("|") : [],
        media: [],
        photo_url: saved.photo_url,
      });
      onClose();
    }
  };

  return (
    <Sheet title="Log a show" onClose={onClose} tall>
      <div style={{display:"flex",flexDirection:"column",gap:12}}>

        {/* Photo capture */}
        <div onClick={()=>fileRef.current?.click()} style={{background:C.bg,border:`2px dashed ${photo?C.brand:C.border}`,borderRadius:12,padding:"16px",textAlign:"center",cursor:"pointer"}}>
          {photo
            ? <img src={photo} alt="show" style={{width:"100%",borderRadius:8,maxHeight:160,objectFit:"cover"}}/>
            : <div>
                <p style={{margin:"0 0 4px",fontSize:22}}>📸</p>
                <p style={{margin:0,fontSize:14,color:C.sub}}>Add a photo from the show</p>
                <p style={{margin:"2px 0 0",fontSize:12,color:C.faint}}>Tap to open camera or gallery</p>
              </div>
          }
        </div>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={handlePhoto} style={{display:"none"}}/>

        <input style={inp} placeholder="Artist" value={f.artist} onChange={e=>set("artist",e.target.value)}/>
        <input style={inp} placeholder="Venue" value={f.venue} onChange={e=>set("venue",e.target.value)}/>
        <input style={{...inp,colorScheme:"dark"}} type="date" value={f.date} onChange={e=>set("date",e.target.value)}/>
        <div>
          <p style={{margin:"0 0 8px",fontSize:13,color:C.sub}}>Rating</p>
          <div style={{display:"flex",gap:8}}>
            {[1,2,3,4,5].map(n=>(
              <button key={n} onClick={()=>set("rating",n)} style={{fontSize:24,background:"none",border:"none",cursor:"pointer",color:n<=f.rating?C.gold:C.border,padding:0}}>{n<=f.rating?"★":"☆"}</button>
            ))}
          </div>
        </div>
        <textarea style={{...inp,minHeight:72,resize:"vertical"}} placeholder="Notes / memories" value={f.notes} onChange={e=>set("notes",e.target.value)}/>
        <textarea style={{...inp,minHeight:100,resize:"vertical"}} placeholder="Setlist — one song per line" value={f.setlist} onChange={e=>set("setlist",e.target.value)}/>
        <textarea style={{...inp,minHeight:60,resize:"vertical"}} placeholder="Grails not played — one per line" value={f.grails} onChange={e=>set("grails",e.target.value)}/>
        {error&&<p style={{fontSize:13,color:C.red,margin:0,textAlign:"center"}}>{error}</p>}
        <button onClick={handleSave} disabled={!valid||saving}
          style={{background:valid?C.brand:"#333",border:"none",borderRadius:12,padding:"14px",color:"#fff",fontSize:16,fontWeight:700,cursor:valid?"pointer":"default",marginTop:4}}>
          {saving?"Saving...":"Save show"}
        </button>
      </div>
    </Sheet>
  );
}

// ─── Edit Show Sheet ──────────────────────────────────────────────────────────
function EditShowSheet({ show, onClose, onUpdate }){
  const [f, setF] = useState({
    artist: show.artist||"",
    venue: show.venue||"",
    date: show.date||"",
    rating: show.rating||0,
    notes: show.notes||"",
    setlist: show.setlist?.join("\n")||"",
    grails: show.grails?.join("\n")||"",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k,v) => setF(p=>({...p,[k]:v}));
  const inp = {width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:10,padding:"12px 14px",color:C.text,fontSize:15,outline:"none",fontFamily:"inherit",boxSizing:"border-box"};
  const valid = f.artist.trim()&&f.date;

  const handleUpdate = async () => {
    if(!valid) return;
    setSaving(true);
    setError("");
    const updates = {
      artist: f.artist.trim(),
      venue: f.venue.trim(),
      show_date: f.date,
      rating: f.rating||3,
      notes: f.notes.trim(),
      setlist: f.setlist.split("\n").map(s=>s.trim()).filter(Boolean).join("|"),
      grails: f.grails.split("\n").map(s=>s.trim()).filter(Boolean).join("|"),
    };
    const { error } = await supabase.from("shows").update(updates).eq("id", show.id);
    if(error){
      setError("Could not update show. Please try again.");
      setSaving(false);
    } else {
      onUpdate({
        ...show,
        ...updates,
        date: updates.show_date,
        setlist: updates.setlist ? updates.setlist.split("|") : [],
        grails: updates.grails ? updates.grails.split("|") : [],
      });
      onClose();
    }
  };

  return (
    <Sheet title="Edit show" onClose={onClose} tall>
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <input style={inp} placeholder="Artist" value={f.artist} onChange={e=>set("artist",e.target.value)}/>
        <input style={inp} placeholder="Venue" value={f.venue} onChange={e=>set("venue",e.target.value)}/>
        <input style={{...inp,colorScheme:"dark"}} type="date" value={f.date} onChange={e=>set("date",e.target.value)}/>
        <div>
          <p style={{margin:"0 0 8px",fontSize:13,color:C.sub}}>Rating</p>
          <div style={{display:"flex",gap:8}}>
            {[1,2,3,4,5].map(n=>(
              <button key={n} onClick={()=>set("rating",n)} style={{fontSize:24,background:"none",border:"none",cursor:"pointer",color:n<=f.rating?C.gold:C.border,padding:0}}>{n<=f.rating?"★":"☆"}</button>
            ))}
          </div>
        </div>
        <textarea style={{...inp,minHeight:72,resize:"vertical"}} placeholder="Notes / memories" value={f.notes} onChange={e=>set("notes",e.target.value)}/>
        <textarea style={{...inp,minHeight:100,resize:"vertical"}} placeholder="Setlist — one song per line" value={f.setlist} onChange={e=>set("setlist",e.target.value)}/>
        <textarea style={{...inp,minHeight:60,resize:"vertical"}} placeholder="Grails not played — one per line" value={f.grails} onChange={e=>set("grails",e.target.value)}/>
        {error&&<p style={{fontSize:13,color:C.red,margin:0,textAlign:"center"}}>{error}</p>}
        <button onClick={handleUpdate} disabled={!valid||saving}
          style={{background:valid?C.brand:"#333",border:"none",borderRadius:12,padding:"14px",color:"#fff",fontSize:16,fontWeight:700,cursor:valid?"pointer":"default",marginTop:4}}>
          {saving?"Saving...":"Update show"}
        </button>
      </div>
    </Sheet>
  );
}


function UpcomingTab({ onSelect, location }){
  const [concerts, setConcerts] = useState(UPCOMING);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(false);
  const [browseCity, setBrowseCity] = useState("");
  const [activeCity, setActiveCity] = useState("");
  const trendColor = t => t==="rising"?C.red:t==="falling"?C.green:C.gold;
  const trendIcon  = t => t==="rising"?"↑":t==="falling"?"↓":"→";

  const pickImage = list => (list||[]).filter(i=>i.ratio==="16_9" && i.width>=500).sort((a,b)=>a.width-b.width)[0]?.url;
  const [searchMode,setSearchMode]=useState("city");
  const fetchConcerts = (city) => {
    setLoading(true);
        const cityParam = city ? `?q=${encodeURIComponent(city)}` : "";
    fetch(`https://www.wazat.app/api/concerts${cityParam}`)
      .then(r=>r.json())
      .then(data=>{
      const events = data?._embedded?.events;  
       setSearchMode(data?.searchMode || "city");
        if(events && events.length > 0){
          // Use local date string comparison to avoid timezone issues
          const now = new Date(); const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}`;
          const futureEvents = events.filter(e => {
            const eventDate = e.dates?.start?.localDate || "";
            return eventDate >= todayStr;
          });
          setConcerts(dedupeImages(futureEvents.map(e=>({
            id: e.id,
            artist: e.name,
            venue: e._embedded?.venues?.[0]?.name || city || "Toronto",
            city: e._embedded?.venues?.[0]?.city?.name || city || "Toronto",
            date: e.dates?.start?.localDate || "",
            daysAway: Math.max(0,Math.ceil((new Date(e.dates?.start?.localDate)-new Date())/(1000*60*60*24))),
            low: e.priceRanges?.[0]?.min ? Math.round(e.priceRanges[0].min) : null,
            high: e.priceRanges?.[0]?.max ? Math.round(e.priceRanges[0].max) : null,
            avg: e.priceRanges?.[0]?.min ? Math.round((e.priceRanges[0].min+(e.priceRanges[0].max||e.priceRanges[0].min))/2) : null,
            trend:"stable", pct:0, grailChance:0, friends:[],
            url: e.url, venueType:"arena", concertSetlist:[], seatmap: e.seatmap?.staticUrl || null,
            image: pickImage(e._embedded?.attractions?.[0]?.images) || pickImage(e.images) || null,attrId: e._embedded?.attractions?.[0]?.id || e.name,
            artistName: e._embedded?.attractions?.[0]?.name || e.name,
          }))));
          setLive(true);
        } else {
          setConcerts([]);
        }
        setLoading(false);
      })
      .catch(()=>setLoading(false));
  };

  useEffect(()=>{ fetchConcerts(""); },[]);

  const handleCitySearch = () => {
    const city = browseCity.trim() || displayCity;
    setActiveCity(city);
    fetchConcerts(city);
};

  const handleReset = () => {
    setBrowseCity("");
    setActiveCity("");
    fetchConcerts("");
  };

  const displayCity = activeCity || location || "Toronto, ON";

  if(loading) return <p style={{textAlign:"center",color:C.sub,padding:"60px 0",fontSize:15}}>Loading concerts... 🎤</p>;

  return (
    <div>
      {/* City search bar */}
      <div style={{display:"flex",gap:8,marginBottom:12}}>
        <div style={{position:"relative",flex:1}}>
        <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",fontSize:14}}>{searchMode==="artist"&&activeCity?"🎤":"📍"}</span>
          <input
            value={browseCity}
            onChange={e=>setBrowseCity(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&handleCitySearch()}
            placeholder="City or artist"
            style={{width:"100%",background:C.card,border:`1px solid ${activeCity?C.brand:C.border}`,borderRadius:10,padding:"10px 10px 10px 30px",color:C.text,fontSize:14,outline:"none",fontFamily:"inherit",boxSizing:"border-box"}}
          />
        </div>
        <button onClick={handleCitySearch} style={{background:C.brand,border:"none",borderRadius:10,padding:"10px 16px",color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",flexShrink:0}}>
          Go
        </button>
        {activeCity&&<button onClick={handleReset} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 12px",color:C.sub,fontSize:13,cursor:"pointer",flexShrink:0}}>
          ✕
        </button>}
      </div>
      {searchMode==="artist"&&activeCity&&concerts.length>0&&<p style={{margin:"0 0 12px",fontSize:13,color:C.sub}}>🎤 Showing "{activeCity}" in all cities</p>}
      {activeCity&&concerts.length===0&&<p style={{margin:"0 0 12px",fontSize:13,color:C.sub}}>No shows found for "{activeCity}". Try another city or artist.</p>}
      
      <p style={{margin:"0 0 16px",fontSize:13,color:C.sub}}>
        📍 {displayCity} {live?"· live from Ticketmaster":"· sample data"}
        {activeCity&&<span style={{color:C.brand}}> · browsing</span>}
      </p>
      {concerts.map(show=>(
        <div key={show.id} style={{border:`1px solid ${C.border}`,borderRadius:14,padding:"14px 16px",marginBottom:10}}>
                 {show.image
  ? <img src={show.image} alt={show.artist} loading="lazy" style={{display:"block",width:"calc(100% + 32px)",height:150,objectFit:"cover",margin:"-14px -16px 12px",borderRadius:"13px 13px 0 0"}}/>
  : <div style={{display:"flex",alignItems:"center",justifyContent:"center",width:"calc(100% + 32px)",height:150,margin:"-14px -16px 12px",borderRadius:"13px 13px 0 0",background:placeholderBg(show.artist),color:"rgba(255,255,255,0.9)",fontSize:48,fontWeight:700,letterSpacing:2}}>{initials(show.artist)}</div>
}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
            <div style={{flex:1,minWidth:0}}>
              <p style={{margin:0,fontSize:16,fontWeight:600,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{show.artist}</p>
              <p style={{margin:"2px 0 4px",fontSize:13,color:C.sub}}>{show.venue}</p>
              <p style={{margin:0,fontSize:12,color:C.sub}}>{fmtDate(show.date)}{daysUntil(show.date)>0?` · ${daysUntil(show.date)} days`:""}</p>
            </div>
            <div style={{textAlign:"right",flexShrink:0,marginLeft:10}}>
              {show.low
                ?<><p style={{margin:0,fontSize:17,fontWeight:700,color:C.text}}>from ${show.low}</p>
                  <p style={{margin:"2px 0 0",fontSize:12,color:trendColor(show.trend),fontWeight:600}}>{trendIcon(show.trend)} {show.trend==="rising"?`+${show.pct}%`:show.trend==="falling"?`${show.pct}%`:"stable"}</p></>
                :<p style={{margin:0,fontSize:13,color:C.sub}}>See prices</p>
              }
            </div>
          </div>
          {(show.friends?.length>0||show.grailChance>0)&&(
            <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap"}}>
              {show.friends?.length>0&&<Pill>👥 {show.friends.join(", ")}</Pill>}
              {show.grailChance>0&&<Pill color={C.gold}>✦ {show.grailChance}% grail</Pill>}
            </div>
          )}
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>onSelect(show)}
              style={{flex:1,background:"none",border:`1px solid ${C.border}`,borderRadius:10,padding:"11px",color:C.sub,fontSize:13,fontWeight:600,cursor:"pointer"}}>
              Details & Setlist
            </button>
            <button onClick={()=>show.url?window.open(show.url,"_blank"):null}
              style={{flex:2,background:C.brand,border:"none",borderRadius:10,padding:"11px",color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer"}}>
              {show.low?`Tickets from $${show.low}`:"Get Tickets"}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ShowsTab({ shows, onSelect, onDelete, onEdit, loading, goingShows }){
  const [search, setSearch] = useState("");
  const filtered = shows.filter(s =>
    s.artist.toLowerCase().includes(search.toLowerCase()) ||
    (s.venue||"").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {/* Going shows at top */}
      {goingShows?.length>0&&<>
        <p style={{margin:"0 0 10px",fontSize:11,fontWeight:700,letterSpacing:2,color:C.green,textTransform:"uppercase"}}>🎟 You're going</p>
        {goingShows.map(show=>(
          <div key={show.id} style={{background:`${C.green}10`,border:`1px solid ${C.green}33`,borderRadius:12,padding:"12px 14px",marginBottom:10}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}} onClick={()=>onSelect(show)}>
              <div style={{flex:1,cursor:"pointer"}}>
                <p style={{margin:0,fontSize:15,fontWeight:700,color:C.text}}>{show.artist}</p>
                <p style={{margin:"2px 0 0",fontSize:13,color:C.sub}}>{show.venue}</p>
                <p style={{margin:"2px 0 0",fontSize:12,color:C.green}}>{fmtDate(show.date)} · {Math.max(0,Math.ceil((new Date(show.date)-new Date())/(1000*60*60*24)))} days away</p>
                <p style={{margin:"6px 0 0",fontSize:12,color:C.faint}}>Tap to view setlist & videos →</p>
              </div>
              <span style={{fontSize:22}}>🎟</span>
            </div>
            {/* Edit & Delete buttons */}
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>onEdit(show)} style={{flex:1,background:"none",border:`1px solid ${C.border}`,borderRadius:8,padding:"7px",color:C.sub,fontSize:13,cursor:"pointer"}}>
                ✏️ Edit
              </button>
              <button onClick={()=>onDelete(show.id)} style={{flex:1,background:"none",border:`1px solid ${C.red}44`,borderRadius:8,padding:"7px",color:C.red,fontSize:13,cursor:"pointer"}}>
                🗑 Remove
              </button>
            </div>
          </div>
        ))}
        <div style={{height:1,background:C.border,margin:"8px 0 16px"}}/>
      </>}

      {/* Stats */}
      <div style={{display:"flex",gap:16,marginBottom:16}}>
        {[{l:"Shows",v:shows.length},{l:"Artists",v:new Set(shows.map(s=>s.artist)).size},{l:"Songs",v:shows.reduce((a,s)=>a+s.setlist.length,0)}].map(s=>(
          <div key={s.l} style={{flex:1,textAlign:"center"}}>
            <p style={{margin:0,fontSize:24,fontWeight:700,color:C.brand}}>{s.v}</p>
            <p style={{margin:0,fontSize:12,color:C.faint}}>{s.l}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div style={{position:"relative",marginBottom:14}}>
        <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:C.faint,fontSize:15}}>🔍</span>
        <input
          value={search}
          onChange={e=>setSearch(e.target.value)}
          placeholder="Search artist or venue..."
          style={{width:"100%",background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:"11px 14px 11px 36px",color:C.text,fontSize:14,outline:"none",fontFamily:"inherit",boxSizing:"border-box"}}
        />
        {search&&<button onClick={()=>setSearch("")} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:C.faint,cursor:"pointer",fontSize:16}}>✕</button>}
      </div>

      <Divider/>
      <div style={{marginTop:12}}>
        {loading&&<p style={{textAlign:"center",color:C.sub,padding:"40px 0",fontSize:15}}>Loading your shows...</p>}
        {!loading&&shows.length===0&&<p style={{textAlign:"center",color:C.faint,padding:"40px 0",fontSize:15}}>No shows yet. Tap + Log to add your first one.</p>}
        {!loading&&shows.length>0&&filtered.length===0&&<p style={{textAlign:"center",color:C.faint,padding:"40px 0",fontSize:15}}>No shows match "{search}"</p>}
        {filtered.map(show=>(
          <div key={show.id} style={{padding:"14px 0",borderBottom:`1px solid ${C.border}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}} onClick={()=>onSelect(show)}>
              <div style={{flex:1,cursor:"pointer"}}>
                <p style={{margin:0,fontSize:16,fontWeight:600,color:C.text}}>{show.artist} {show.photo_url&&"📸"}</p>
                <p style={{margin:"2px 0 0",fontSize:13,color:C.sub}}>{show.venue} · {fmtShort(show.date)}</p>
                {show.grails?.length>0&&<p style={{margin:"4px 0 0",fontSize:12,color:C.gold}}>✦ {show.grails.length} grail{show.grails.length>1?"s":""}</p>}
              </div>
              <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4,cursor:"pointer"}}>
                <Stars n={show.rating}/>
                <span style={{color:C.faint,fontSize:18}}>›</span>
              </div>
            </div>
            {/* Edit, Delete & Share buttons */}
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>onEdit(show)} style={{flex:1,background:"none",border:`1px solid ${C.border}`,borderRadius:8,padding:"7px",color:C.sub,fontSize:13,cursor:"pointer"}}>
                ✏️ Edit
              </button>
              <button onClick={()=>{
                const text = `I saw ${show.artist} at ${show.venue} 🎤\nencore-d55e.vercel.app`;
                if(navigator.share){ navigator.share({title:"Encore",text,url:"https://encore-d55e.vercel.app"}); }
                else { navigator.clipboard.writeText(text); alert("Copied! Paste anywhere to share."); }
              }} style={{flex:1,background:"none",border:`1px solid ${C.border}`,borderRadius:8,padding:"7px",color:C.sub,fontSize:13,cursor:"pointer"}}>
                ↑ Share
              </button>
              <button onClick={()=>onDelete(show.id)} style={{flex:1,background:"none",border:`1px solid ${C.red}44`,borderRadius:8,padding:"7px",color:C.red,fontSize:13,cursor:"pointer"}}>
                🗑 Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FriendsTab({ onOpenMonetize, session }){
  const [inviting, setInviting] = useState(false);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load existing friends
  useEffect(()=>{
    if(!session) return;
    loadFriends();
  },[session]);

  const loadFriends = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("friendships")
      .select("friend_id, status")
      .eq("user_id", session.user.id);
    if(data){
      // Get profile for each friend
      const friendProfiles = await Promise.all(data.map(async f => {
        const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/rest/v1/profiles?id=eq.${f.friend_id}&select=id,email,tier`, {
          headers: { "apikey": process.env.REACT_APP_SUPABASE_ANON_KEY, "Authorization": `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}` }
        });
        const profile = await res.json();
        // Get show count
        const { count } = await supabase.from("shows").select("id", {count:"exact"}).eq("user_id", f.friend_id);
        return { ...profile[0], status: f.status, shows: count||0 };
      }));
      setFriends(friendProfiles.filter(Boolean));
    }
    setLoading(false);
  };

  const handleSearch = async () => {
    if(!search.trim()) return;
    setSearching(true);
    setSearchResults([]);
    // Search profiles by email
    const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/rest/v1/profiles?email=ilike.*${encodeURIComponent(search.trim())}*&select=id,email,tier`, {
      headers: { "apikey": process.env.REACT_APP_SUPABASE_ANON_KEY, "Authorization": `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}` }
    });
    const data = await res.json();
    // Filter out self and existing friends
    const filtered = (data||[]).filter(u => u.id !== session.user.id && !friends.find(f=>f.id===u.id));
    setSearchResults(filtered);
    setSearching(false);
  };

  const handleAddFriend = async (user) => {
    const { error } = await supabase.from("friendships").insert([{
      user_id: session.user.id,
      friend_id: user.id,
      status: "active"
    }]);
    if(!error){
      setFriends(p=>[...p, {...user, status:"active", shows:0}]);
      setSearchResults(p=>p.filter(u=>u.id!==user.id));
    }
  };

  const handleRemoveFriend = async (friendId) => {
    await supabase.from("friendships").delete().eq("user_id", session.user.id).eq("friend_id", friendId);
    setFriends(p=>p.filter(f=>f.id!==friendId));
  };

  const inp = {width:"100%",background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:"11px 14px",color:C.text,fontSize:14,outline:"none",fontFamily:"inherit",boxSizing:"border-box"};

  return (
    <div>
      {/* Invite via WhatsApp */}
      <button onClick={()=>setInviting(true)} style={{width:"100%",background:`${C.wa}15`,border:`1px solid ${C.wa}44`,borderRadius:12,padding:"13px",color:C.wa,fontSize:15,fontWeight:700,cursor:"pointer",marginBottom:20,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
        💬 Invite friends via WhatsApp
      </button>

      {/* Search for friends */}
      <p style={{margin:"0 0 10px",fontSize:11,fontWeight:700,letterSpacing:2,color:C.faint,textTransform:"uppercase"}}>Find friends on Wazat</p>
      <div style={{display:"flex",gap:8,marginBottom:16}}>
        <input
          style={{...inp, flex:1}}
          placeholder="Search by email..."
          value={search}
          onChange={e=>setSearch(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&handleSearch()}
        />
        <button onClick={handleSearch} disabled={searching} style={{background:C.brand,border:"none",borderRadius:10,padding:"11px 16px",color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",flexShrink:0}}>
          {searching?"...":"Search"}
        </button>
      </div>

      {/* Search results */}
      {searchResults.length>0&&(
        <div style={{marginBottom:20}}>
          <p style={{margin:"0 0 8px",fontSize:12,color:C.sub}}>{searchResults.length} user{searchResults.length>1?"s":""} found</p>
          {searchResults.map(user=>(
            <div key={user.id} style={{display:"flex",alignItems:"center",gap:12,padding:"12px",background:C.card,borderRadius:12,marginBottom:8,border:`1px solid ${C.border}`}}>
              <div style={{width:40,height:40,borderRadius:"50%",background:C.brand,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:700,color:"#fff",flexShrink:0}}>
                {user.email?.slice(0,2).toUpperCase()}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <p style={{margin:0,fontSize:14,fontWeight:600,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user.email}</p>
                <p style={{margin:0,fontSize:12,color:C.sub}}>{user.tier==="superfan"?"✦ Superfan":user.tier==="pro"?"⭐ Pro":"Free"}</p>
              </div>
              <button onClick={()=>handleAddFriend(user)} style={{background:C.brand,border:"none",borderRadius:8,padding:"8px 14px",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",flexShrink:0}}>
                + Add
              </button>
            </div>
          ))}
        </div>
      )}
      {search&&searchResults.length===0&&!searching&&(
        <p style={{fontSize:13,color:C.faint,marginBottom:16,textAlign:"center"}}>No users found. Make sure they have a Wazat account!</p>
      )}

      {/* Friends list */}
      <p style={{margin:"0 0 10px",fontSize:11,fontWeight:700,letterSpacing:2,color:C.faint,textTransform:"uppercase"}}>
        Your friends {friends.length>0&&`(${friends.length})`}
      </p>

      {loading&&<p style={{textAlign:"center",color:C.sub,padding:"20px 0"}}>Loading friends...</p>}

      {!loading&&friends.length===0&&(
        <div style={{textAlign:"center",padding:"24px 0",background:C.card,borderRadius:12,border:`1px solid ${C.border}`}}>
          <p style={{margin:"0 0 4px",fontSize:22}}>🎤</p>
          <p style={{margin:"0 0 4px",fontSize:15,color:C.sub}}>No friends yet</p>
          <p style={{margin:0,fontSize:13,color:C.faint}}>Search for friends above or invite via WhatsApp</p>
        </div>
      )}

      {friends.map(f=>(
        <div key={f.id} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 0",borderBottom:`1px solid ${C.border}`}}>
          <div style={{width:42,height:42,borderRadius:"50%",background:C.brand,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:700,color:"#fff",flexShrink:0}}>
            {f.email?.slice(0,2).toUpperCase()}
          </div>
          <div style={{flex:1,minWidth:0}}>
            <p style={{margin:0,fontSize:14,fontWeight:600,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.email}</p>
            <p style={{margin:0,fontSize:12,color:C.sub}}>{f.shows} shows logged · {f.tier==="superfan"?"✦ Superfan":f.tier==="pro"?"⭐ Pro":"Free"}</p>
          </div>
          <button onClick={()=>handleRemoveFriend(f.id)} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:8,padding:"6px 10px",color:C.faint,fontSize:12,cursor:"pointer",flexShrink:0}}>
            Remove
          </button>
        </div>
      ))}

      {inviting&&<WhatsAppSheet onClose={()=>setInviting(false)}/>}
    </div>
  );
}

// ─── Login Screen ─────────────────────────────────────────────────────────────
function LoginScreen(){
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const inp = {width:"100%",background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:"12px 14px",color:C.text,fontSize:15,outline:"none",fontFamily:"inherit",boxSizing:"border-box"};

  const handleSubmit = async () => {
    setLoading(true);
    setMessage("");
    if(isSignUp){
      const { error } = await supabase.auth.signUp({ email, password });
      if(error) setMessage(error.message);
      else setMessage("Check your email to confirm your account!");
    } else {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if(error) setMessage(error.message);
    }
    setLoading(false);
  };

  return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{width:"100%",maxWidth:380}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{width:90,height:56,margin:"0 auto 16px",borderRadius:14,background:C.brand,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <svg width="70" height="42" viewBox="0 0 52 32">
              <rect x="0" y="0" width="52" height="32" rx="4" fill="#0f0f0f"/>
              <rect x="0" y="0" width="13" height="32" rx="4" fill="#1a1a1a"/>
              <line x1="13" y1="0" x2="13" y2="32" stroke="#e8651a" strokeWidth="1" strokeDasharray="3,3" opacity="0.4"/>
              <circle cx="0" cy="0" r="6" fill="#e8651a"/>
              <circle cx="52" cy="0" r="6" fill="#e8651a"/>
              <circle cx="0" cy="32" r="6" fill="#e8651a"/>
              <circle cx="52" cy="32" r="6" fill="#e8651a"/>
              <text x="7" y="14" fontFamily="sans-serif" fontSize="5" fontWeight="700" fill="#e8651a" textAnchor="middle" opacity="0.7">WAS</text>
              <text x="7" y="21" fontFamily="sans-serif" fontSize="5" fontWeight="700" fill="#e8651a" textAnchor="middle" opacity="0.7">AT</text>
              <text x="33" y="21" fontFamily="sans-serif" fontSize="18" fontWeight="900" fill="#e8651a" textAnchor="middle">W</text>
            </svg>
          </div>
          <h1 style={{fontSize:28,fontWeight:700,color:C.text,margin:"0 0 4px",letterSpacing:"-0.5px"}}>wazat</h1>
          <p style={{fontSize:11,fontWeight:700,color:C.brand,margin:"0 0 6px",letterSpacing:3}}>I WAS AT THAT SHOW</p>
          <p style={{fontSize:13,color:C.sub,margin:0}}>Your live music journal</p>
        </div>
        <form onSubmit={e=>{e.preventDefault();handleSubmit();}} style={{display:"flex",flexDirection:"column",gap:12}}>
          <input
            style={inp}
            type="email"
            placeholder="Email"
            value={email}
            onChange={e=>setEmail(e.target.value)}
            autoComplete="email"
            name="email"
          />
          <input
            style={inp}
            type="password"
            placeholder="Password"
            value={password}
            onChange={e=>setPassword(e.target.value)}
            autoComplete={isSignUp?"new-password":"current-password"}
            name="password"
          />
          {message&&<p style={{fontSize:13,color:message.includes("Check")?C.green:C.red,margin:0,textAlign:"center"}}>{message}</p>}
          <button type="submit" disabled={loading||!email||!password} style={{background:C.brand,border:"none",borderRadius:12,padding:"14px",color:"#fff",fontSize:16,fontWeight:700,cursor:"pointer",marginTop:4}}>
            {loading?"...":(isSignUp?"Create account":"Sign in")}
          </button>
          <button type="button" onClick={()=>{setIsSignUp(s=>!s);setMessage("");}} style={{background:"none",border:"none",color:C.sub,fontSize:14,cursor:"pointer",padding:8}}>
            {isSignUp?"Already have an account? Sign in":"Don't have an account? Sign up"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Onboarding Sheet ────────────────────────────────────────────────────────
function OnboardingSheet({ onClose, onLog, onExplore }){
  const [step, setStep] = useState(0);
  const steps = [
    { icon:"🎤", title:"Welcome to Wazat", body:"Your personal live music journal. Log every show, discover setlists, find tickets and connect with fans." },
    { icon:"📝", title:"Log your shows", body:"Tap + Log to add concerts you've been to. Rate them, add setlists, track songs you hoped to hear." },
    { icon:"🎬", title:"Videos auto-load", body:"Add a setlist and Wazat automatically finds live YouTube videos for every song. No searching needed." },
    { icon:"🎟", title:"Discover & buy tickets", body:"Browse upcoming concerts near you with real prices. Tap I'm going to save them and get videos pre-loaded." },
  ];
  const current = steps[step];
  const isLast = step === steps.length - 1;

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:300,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
      <div style={{background:C.card,borderRadius:"20px 20px 0 0",width:"100%",maxWidth:480,padding:"32px 24px 48px"}}>
        <div style={{textAlign:"center",marginBottom:28}}>
          <div style={{fontSize:56,marginBottom:16}}>{current.icon}</div>
          <p style={{margin:"0 0 10px",fontSize:22,fontWeight:700,color:C.text}}>{current.title}</p>
          <p style={{margin:0,fontSize:15,color:C.sub,lineHeight:1.6}}>{current.body}</p>
        </div>

        {/* Step dots */}
        <div style={{display:"flex",justifyContent:"center",gap:6,marginBottom:24}}>
          {steps.map((_,i)=>(
            <div key={i} style={{width:i===step?20:6,height:6,borderRadius:3,background:i===step?C.brand:C.border,transition:"all 0.3s"}}/>
          ))}
        </div>

        {isLast ? (
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <button onClick={onLog} style={{background:C.brand,border:"none",borderRadius:12,padding:"14px",color:"#fff",fontSize:16,fontWeight:700,cursor:"pointer"}}>
              📝 Log your first show
            </button>
            <button onClick={onExplore} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:12,padding:"13px",color:C.sub,fontSize:15,cursor:"pointer"}}>
              🎟 Explore upcoming concerts
            </button>
          </div>
        ) : (
          <div style={{display:"flex",gap:10}}>
            <button onClick={onClose} style={{flex:1,background:"none",border:`1px solid ${C.border}`,borderRadius:12,padding:"13px",color:C.sub,fontSize:14,cursor:"pointer"}}>
              Skip
            </button>
            <button onClick={()=>setStep(s=>s+1)} style={{flex:2,background:C.brand,border:"none",borderRadius:12,padding:"13px",color:"#fff",fontSize:15,fontWeight:700,cursor:"pointer"}}>
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Location Sheet ───────────────────────────────────────────────────────────
function LocationSheet({ current, onClose, onSelect }){
  const [search, setSearch] = useState("");
  const cities = [
    "Toronto, ON","Vancouver, BC","Montreal, QC","Calgary, AB","Ottawa, ON",
    "Edmonton, AB","Winnipeg, MB","Quebec City, QC","Halifax, NS","Victoria, BC",
    "New York, NY","Los Angeles, CA","Chicago, IL","Austin, TX","Nashville, TN",
    "London, UK","Manchester, UK","Sydney, AU","Melbourne, AU","Toronto, ON",
  ].filter((v,i,a)=>a.indexOf(v)===i);
  const filtered = cities.filter(c=>c.toLowerCase().includes(search.toLowerCase()));

  return (
    <Sheet title="Change location" onClose={onClose}>
      <div style={{position:"relative",marginBottom:14}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search city..."
          style={{width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:10,padding:"11px 14px",color:C.text,fontSize:14,outline:"none",fontFamily:"inherit",boxSizing:"border-box"}}/>
      </div>
      {filtered.map(city=>(
        <div key={city} onClick={()=>{ onSelect(city); localStorage.setItem("wazat_location", city); onClose(); }}
          style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"13px 0",borderBottom:`1px solid ${C.border}`,cursor:"pointer"}}>
          <span style={{fontSize:15,color:C.text}}>📍 {city}</span>
          {city===current&&<span style={{color:C.brand,fontSize:13,fontWeight:600}}>✓ Current</span>}
        </div>
      ))}
    </Sheet>
  );
}

// ─── Profile Tab ──────────────────────────────────────────────────────────────
function ProfileTab({ session, shows, goingShows, isPro, location, onLocationChange, onOpenMonetize, onSignOut, onShowGuide }){
  const [showLocation, setShowLocation] = useState(false);
  const [pickingAvatar, setPickingAvatar] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const deleteAccount = async () => {
    setDeleting(true); setDeleteError("");
    try {
      const { data: sd } = await supabase.auth.getSession();
      const token = sd?.session?.access_token;
      const r = await fetch("https://www.wazat.app/api/delete-account", { method:"POST", headers:{ Authorization:`Bearer ${token}` } });
      if(!r.ok) throw new Error("delete failed");
      try{ localStorage.clear(); }catch(e){}
      await supabase.auth.signOut();
    } catch(e){
      setDeleteError("Could not delete your account. Please try again or email hello@wazat.app.");
      setDeleting(false);
    }
  };
  const [avatar, setAvatar] = useState(()=>{ try{ return JSON.parse(localStorage.getItem("wazat_avatar")||"null"); }catch(e){ return null; } });
  const [notifEnabled, setNotifEnabled] = useState(typeof Notification !== "undefined" && Notification.permission === "granted");
  const email = session?.user?.email || "";
  const initials = email.slice(0,2).toUpperCase();
  const totalSongs = shows.reduce((a,s)=>a+s.setlist.length,0);
  const totalArtists = new Set(shows.map(s=>s.artist)).size;

  const isOwner = OWNER_IDS.includes(session?.user?.id);
  const tierColor = isOwner ? C.gold : isPro ? C.brand : C.sub;
  const tierLabel = isOwner ? "✦ Superfan" : "Member";

  return (
    <div>
            {/* Avatar & name */}
      <div style={{textAlign:"center",padding:"20px 0 24px"}}>
        <div onClick={()=>setPickingAvatar(true)} style={{width:72,height:72,borderRadius:"50%",background:avatar?.color||C.brand,display:"flex",alignItems:"center",justifyContent:"center",fontSize:avatar?36:26,fontWeight:700,color:"#fff",margin:"0 auto 6px",cursor:"pointer"}}>
          {avatar?.emoji||initials}
        </div>
        <p onClick={()=>setPickingAvatar(true)} style={{margin:"0 0 10px",fontSize:12,color:C.brand,cursor:"pointer"}}>Change avatar</p>
        <p style={{margin:"0 0 4px",fontSize:18,fontWeight:700,color:C.text}}>{email}</p>
        <div style={{display:"inline-flex",alignItems:"center",gap:6,background:`${tierColor}18`,border:`1px solid ${tierColor}44`,borderRadius:20,padding:"4px 12px"}}>
          <span style={{fontSize:12,fontWeight:700,color:tierColor}}>{tierLabel}</span>
        </div>
      </div>

      {/* Stats */}
      <div style={{display:"flex",gap:0,marginBottom:20,background:C.card,borderRadius:14,overflow:"hidden",border:`1px solid ${C.border}`}}>
        {[{l:"Shows",v:shows.length},{l:"Artists",v:totalArtists},{l:"Songs",v:totalSongs},{l:"Going",v:goingShows.length}].map((s,i)=>(
          <div key={s.l} style={{flex:1,textAlign:"center",padding:"16px 0",borderRight:i<3?`1px solid ${C.border}`:"none"}}>
            <p style={{margin:0,fontSize:22,fontWeight:700,color:C.brand}}>{s.v}</p>
            <p style={{margin:0,fontSize:11,color:C.faint}}>{s.l}</p>
          </div>
        ))}
      </div>

      {/* Settings */}
      <p style={{margin:"0 0 10px",fontSize:11,fontWeight:700,letterSpacing:2,color:C.faint,textTransform:"uppercase"}}>Settings</p>

      {/* Location */}
      <div onClick={()=>setShowLocation(true)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 0",borderBottom:`1px solid ${C.border}`,cursor:"pointer"}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:18}}>📍</span>
          <div>
            <p style={{margin:0,fontSize:15,color:C.text}}>Location</p>
            <p style={{margin:0,fontSize:12,color:C.sub}}>Upcoming concerts near you</p>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:6}}>
          <span style={{fontSize:13,color:C.brand,fontWeight:600}}>{location}</span>
          <span style={{color:C.faint}}>›</span>
        </div>
      </div>

      {/* Notifications */}
      <div onClick={async ()=>{
        try {
          if(!("serviceWorker" in navigator) || !("PushManager" in window)){
            alert("Push notifications not supported on this browser.");
            return;
          }
          const permission = await Notification.requestPermission();
          if(permission !== "granted"){
            alert("Please allow notifications in your browser settings.");
            return;
          }
          const reg = await navigator.serviceWorker.ready;
          const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
          let sub = await reg.pushManager.getSubscription();
          if(!sub){
            sub = await reg.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: urlBase64ToUint8Array(vapidKey)
            });
          }
          const res = await fetch("/api/subscribe", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ subscription: sub.toJSON(), user_id: session.user.id })
          });
          if(res.ok){ setNotifEnabled(true); alert("Notifications enabled! ✅ You'll get alerts for price drops and show reminders."); }
          else alert("Could not enable notifications. Please try again.");
        } catch(err) {
          alert("Error: " + err.message);
        }
      }} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 0",borderBottom:`1px solid ${C.border}`,cursor:"pointer"}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:18}}>🔔</span>
          <div>
            <p style={{margin:0,fontSize:15,color:C.text}}>Notifications</p>
            <p style={{margin:0,fontSize:12,color:C.sub}}>Price drops, show reminders</p>
          </div>
        </div>
        <span style={{fontSize:13,color:notifEnabled?C.green:C.brand,fontWeight:600}}>{notifEnabled?"✓ Enabled":"Enable ›"}</span>
      </div>

      {/* Upgrade */}
      {!isPro&&<div onClick={onOpenMonetize} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 0",borderBottom:`1px solid ${C.border}`,cursor:"pointer"}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:18}}>💎</span>
          <div>
            <p style={{margin:0,fontSize:15,color:C.text}}>Upgrade to Pro</p>
            <p style={{margin:0,fontSize:12,color:C.sub}}>Setlists, videos, media sharing</p>
          </div>
        </div>
        <span style={{fontSize:13,color:C.brand,fontWeight:700}}>$4.99/mo ›</span>
      </div>}

      {/* How Wazat Works */}
      <div onClick={onShowGuide} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 0",borderBottom:`1px solid ${C.border}`,cursor:"pointer"}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:18}}>💡</span>
          <div>
            <p style={{margin:0,fontSize:15,color:C.text}}>How Wazat works</p>
            <p style={{margin:0,fontSize:12,color:C.sub}}>Quick guide to get started</p>
          </div>
        </div>
        <span style={{color:C.faint}}>›</span>
      </div>

      {/* Privacy Policy */}
      <div onClick={()=>window.open("https://www.wazat.app/privacy.html","_blank")} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 0",borderBottom:`1px solid ${C.border}`,cursor:"pointer"}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:18}}>🔒</span>
          <p style={{margin:0,fontSize:15,color:C.text}}>Privacy Policy</p>
        </div>
        <span style={{color:C.faint}}>›</span>
      </div>

      {/* Sign out */}
      <div onClick={onSignOut} style={{display:"flex",alignItems:"center",gap:12,padding:"14px 0",borderBottom:`1px solid ${C.border}`,cursor:"pointer"}}>
        <span style={{fontSize:18}}>🚪</span>
        <p style={{margin:0,fontSize:15,color:C.red}}>Sign out</p>
      </div>

     {/* Delete account */}
      <div onClick={()=>setConfirmDelete(true)} style={{display:"flex",alignItems:"center",gap:12,padding:"14px 0",borderBottom:`1px solid ${C.border}`,cursor:"pointer"}}>
        <span style={{fontSize:18}}>🗑️</span>
        <p style={{margin:0,fontSize:15,color:C.sub}}>Delete account</p>
      </div> 
      {/* App info */}
      <div style={{marginTop:32,textAlign:"center"}}>
        <p style={{margin:"0 0 4px",fontSize:13,color:C.faint}}>Wazat · v0.1.0</p>
        <p style={{margin:0,fontSize:12,color:C.faint}}>Made with 🧡 one step at a time</p>
      </div>

      {pickingAvatar&&<AvatarSheet current={avatar} onClose={()=>setPickingAvatar(false)} onSave={a=>{ setAvatar(a); try{ localStorage.setItem("wazat_avatar",JSON.stringify(a)); }catch(e){} setPickingAvatar(false); }}/>}

      {confirmDelete&&<Sheet title="Delete account" onClose={()=>{ if(!deleting) setConfirmDelete(false); }}>
        <p style={{margin:"0 0 12px",fontSize:15,color:C.text,lineHeight:1.5}}>This permanently deletes your account and everything in it: your shows, photos, videos and friends.</p>
        <p style={{margin:"0 0 18px",fontSize:14,color:C.sub,lineHeight:1.5}}>This cannot be undone.</p>
        {deleteError&&<p style={{margin:"0 0 14px",fontSize:13,color:C.red}}>{deleteError}</p>}
        <button onClick={deleteAccount} disabled={deleting} style={{width:"100%",background:C.red,border:"none",borderRadius:12,padding:"14px",color:"#fff",fontSize:15,fontWeight:700,cursor:"pointer",opacity:deleting?0.6:1,marginBottom:10}}>{deleting?"Deleting...":"Yes, delete my account"}</button>
        <button onClick={()=>setConfirmDelete(false)} disabled={deleting} style={{width:"100%",background:"none",border:`1px solid ${C.border}`,borderRadius:12,padding:"14px",color:C.text,fontSize:15,cursor:"pointer"}}>Cancel</button>
      </Sheet>}
      {showLocation&&<LocationSheet current={location} onClose={()=>setShowLocation(false)} onSelect={onLocationChange}/>}
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function WazatApp(){
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showSplash, setShowSplash] = useState(true);
  const [shows, setShows]           = useState([]);
  const [showsLoading, setShowsLoading] = useState(false);
  const [tab, setTab]               = useState("upcoming");
  const [selectedShow, setSelectedShow]       = useState(null);
  const [selectedConcert, setSelectedConcert] = useState(null);
  const [adding, setAdding]         = useState(false);
  const [editing, setEditing]       = useState(null);
  const [goingShows, setGoingShows] = useState([]);
  const [showMonetize, setShowMonetize] = useState(false);
  const isPro = useIsPro();

  // Splash screen — show for 2.2 seconds then fade out
  useEffect(()=>{
    const t = setTimeout(()=>setShowSplash(false), 2200);
    return ()=>clearTimeout(t);
  },[]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Load user tier from profiles table
  useEffect(()=>{
    if(!session) return;
    // Check if owner first
    if(OWNER_IDS.includes(session.user.id)){
      setProGlobal(true);
      _isSuperfan = true;
      return;
    }
    // Otherwise check database
    fetch(`${process.env.REACT_APP_SUPABASE_URL}/rest/v1/profiles?id=eq.${session.user.id}&select=tier`, {
      headers: {
        "apikey": process.env.REACT_APP_SUPABASE_ANON_KEY,
        "Authorization": `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
      }
    })
    .then(r=>r.json())
    .then(data=>{
      const tier = data?.[0]?.tier;
      if(tier === "pro" || tier === "superfan") setProGlobal(true);
      if(tier === "superfan") _isSuperfan = true;
    })
    .catch(console.error);

    // Check if returning from successful Stripe payment
    const params = new URLSearchParams(window.location.search);
    if(params.get("upgraded") === "true"){
      setTimeout(()=>{ window.location.href = window.location.pathname; }, 100);
    }
  },[session]);
  useEffect(() => {
    if(!session) return;
    setShowsLoading(true);
    supabase
      .from("shows")
      .select("*")
      .eq("user_id", session.user.id)
      .order("show_date", { ascending: false })
      .then(({ data, error }) => {
        if(data && !error){
          const mapped = data.map(s=>({
            id: s.id,
            artist: s.artist,
            venue: s.venue,
            city: s.city,
            date: s.show_date,
            rating: s.rating,
            notes: s.notes,
            setlist: s.setlist ? s.setlist.split("|") : [],
            grails: s.grails ? s.grails.split("|") : [],
            media: [],
            status: s.status||"attended",
            photo_url: s.photo_url||null,
          }));
          setShows(mapped.filter(s=>s.status==="attended"||!s.status));
          setGoingShows(mapped.filter(s=>s.status==="going"));
        }
        setShowsLoading(false);
      });
  }, [session]);

  const handleGoing = async (concert) => {
    if(goingShows.find(s=>s.id===concert.id)) return;
    // Save to Supabase as a "going" show
    const { data, error } = await supabase.from("shows").insert([{
      user_id: session.user.id,
      artist: concert.artist,
      venue: concert.venue,
      city: concert.city,
      show_date: concert.date,
      rating: 0,
      notes: "",
      setlist: concert.concertSetlist?.join("|")||"",
      grails: "",
      status: "going",
      event_id: concert.id.toString(),
    }]).select();
    if(!error && data?.[0]){
      const saved = {
        ...concert,
        id: data[0].id,
        date: data[0].show_date,
        setlist: concert.concertSetlist||[],
        grails: [],
        media: [],
        status: "going",
      };
      setGoingShows(p=>[...p, saved]);
      setTab("shows");
    }
  };

  // Register service worker only (no auto push subscription)
  useEffect(()=>{
    if(!("serviceWorker" in navigator)) return;
    navigator.serviceWorker.register("/sw.js").catch(console.error);
  },[]);

  const handleDelete = async (id) => {
    if(!window.confirm("Remove this show? This cannot be undone.")) return;
    const { error } = await supabase.from("shows").delete().eq("id", id);
    if(!error){
      setShows(prev => prev.filter(s => s.id !== id));
      setGoingShows(prev => prev.filter(s => s.id !== id));
    }
  };

  const [location, setLocation] = useState("Toronto, ON");
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Auto-detect location on first load
  useEffect(()=>{
    const savedLocation = localStorage.getItem("wazat_location");
    if(savedLocation){
      setLocation(savedLocation);
      return;
    }
    if(!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (pos)=>{
        try {
          const { latitude, longitude } = pos.coords;
          const res = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`);
          const data = await res.json();
          const city = data.city || data.locality || data.principalSubdivision || "";
          const country = data.countryCode || "";
          if(city){
            const loc = country === "CA" ? `${city}, ON` : country === "US" ? `${city}, US` : `${city}, ${country}`;
            setLocation(loc);
            localStorage.setItem("wazat_location", loc);
          }
        } catch(e){ console.error("Location detection failed:", e); }
      },
      ()=>{ /* User denied — keep default */ },
      { timeout: 8000, maximumAge: 3600000 }
    );
  },[]);

  const handleUpdate = (updated) => {
    setShows(prev => prev.map(s => s.id === updated.id ? updated : s));
    setGoingShows(prev => prev.map(s => s.id === updated.id ? updated : s));
  };

  // Animated splash screen
  if(showSplash) return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:20}}>
      <style>{`
        @keyframes tearLeft {
          0%   { transform: translateX(0) rotate(0deg); opacity:1; }
          60%  { transform: translateX(0) rotate(0deg); opacity:1; }
          100% { transform: translateX(-80px) rotate(-15deg); opacity:0; }
        }
        @keyframes tearRight {
          0%   { transform: translateX(0) rotate(0deg); opacity:1; }
          60%  { transform: translateX(0) rotate(0deg); opacity:1; }
          100% { transform: translateX(80px) rotate(15deg); opacity:0; }
        }
        @keyframes fadeInWazat {
          0%,65% { opacity:0; transform: scale(0.85); }
          100%   { opacity:1; transform: scale(1); }
        }
        @keyframes shakeTear {
          0%,55%  { transform: translateX(0); }
          58%     { transform: translateX(-4px); }
          61%     { transform: translateX(4px); }
          64%     { transform: translateX(-3px); }
          67%     { transform: translateX(0); }
          100%    { transform: translateX(0); }
        }
      `}</style>

      {/* Ticket tear animation */}
      <div style={{position:"relative",width:200,height:120,animation:"shakeTear 2.2s ease-in-out forwards"}}>
        {/* Left stub — tears left */}
        <div style={{position:"absolute",left:0,top:0,width:52,height:120,animation:"tearLeft 2.2s ease-in-out forwards",transformOrigin:"right center"}}>
          <svg width="52" height="120" viewBox="0 0 52 120">
            <rect x="0" y="10" width="52" height="100" rx="8" fill={C.brand}/>
            <circle cx="52" cy="10" r="12" fill={C.bg}/>
            <circle cx="52" cy="110" r="12" fill={C.bg}/>
            <text x="26" y="57" fontFamily="sans-serif" fontSize="9" fontWeight="700" fill="#0f0f0f" textAnchor="middle" opacity="0.7">WAS</text>
            <text x="26" y="70" fontFamily="sans-serif" fontSize="9" fontWeight="700" fill="#0f0f0f" textAnchor="middle" opacity="0.7">AT</text>
            {[10,22,34,46,58,70,82,94,106].map((y,i)=>(
              <rect key={i} x="48" y={y} width="3" height="6" rx="1" fill={C.bg} opacity="0.6"/>
            ))}
          </svg>
        </div>

        {/* Right main body — tears right */}
        <div style={{position:"absolute",left:52,top:0,width:148,height:120,animation:"tearRight 2.2s ease-in-out forwards",transformOrigin:"left center"}}>
          <svg width="148" height="120" viewBox="0 0 148 120">
            <rect x="0" y="10" width="148" height="100" rx="8" fill={C.brand}/>
            <circle cx="0" cy="10" r="12" fill={C.bg}/>
            <circle cx="0" cy="110" r="12" fill={C.bg}/>
            <circle cx="148" cy="10" r="12" fill={C.bg}/>
            <circle cx="148" cy="110" r="12" fill={C.bg}/>
            {[10,22,34,46,58,70,82,94,106].map((y,i)=>(
              <rect key={i} x="0" y={y} width="3" height="6" rx="1" fill={C.bg} opacity="0.6"/>
            ))}
            <text x="74" y="72" fontFamily="sans-serif" fontSize="42" fontWeight="900" fill="#0f0f0f" textAnchor="middle">W</text>
          </svg>
        </div>
      </div>

      {/* Wazat wordmark fades in after tear */}
      <div style={{textAlign:"center",animation:"fadeInWazat 2.2s ease-in-out forwards"}}>
        <div style={{fontSize:32,fontWeight:700,letterSpacing:"-1px",color:C.text}}>wazat</div>
        <div style={{fontSize:10,fontWeight:700,color:C.brand,letterSpacing:3,marginTop:4}}>I WAS AT THAT SHOW</div>
      </div>
    </div>
  );

  if(authLoading) return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <p style={{color:C.sub,fontSize:16}}>Loading...</p>
    </div>
  );

  if(!session) return <LoginScreen/>;

  const TABS = [
    { key:"upcoming", label:"Upcoming" },
    { key:"shows",    label:"My Shows" },
    { key:"friends",  label:"Friends"  },
    { key:"profile",  label:"Profile"  },
  ];

  return (
    <div style={{height:"100vh",background:C.bg,color:C.text,fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",maxWidth:480,margin:"0 auto",display:"flex",flexDirection:"column",overflow:"hidden",position:"fixed",width:"100%",left:"50%",transform:"translateX(-50%)"}}>

      {/* Sticky Header */}
      <div style={{flexShrink:0,zIndex:100,borderBottom:`1px solid ${C.border}`,paddingTop:'env(safe-area-inset-top)'}}>
        {/* Logo row */}
        <div style={{display:"flex",alignItems:"center",height:52}}>
          {/* Orange left — ticket logo */}
          <div style={{background:C.brand,height:"100%",display:"flex",alignItems:"center",justifyContent:"center",padding:"0 14px",minWidth:68}}>
            <svg width="50" height="30" viewBox="0 0 52 32">
              <rect x="0" y="0" width="52" height="32" rx="4" fill="#0f0f0f"/>
              <rect x="0" y="0" width="13" height="32" rx="4" fill="#1a1a1a"/>
              <line x1="13" y1="0" x2="13" y2="32" stroke="#e8651a" strokeWidth="1" strokeDasharray="3,3" opacity="0.4"/>
              <circle cx="0" cy="0" r="6" fill="#e8651a"/>
              <circle cx="52" cy="0" r="6" fill="#e8651a"/>
              <circle cx="0" cy="32" r="6" fill="#e8651a"/>
              <circle cx="52" cy="32" r="6" fill="#e8651a"/>
              <text x="7" y="14" fontFamily="sans-serif" fontSize="5" fontWeight="700" fill="#e8651a" textAnchor="middle" opacity="0.7">WAS</text>
              <text x="7" y="21" fontFamily="sans-serif" fontSize="5" fontWeight="700" fill="#e8651a" textAnchor="middle" opacity="0.7">AT</text>
              <text x="33" y="21" fontFamily="sans-serif" fontSize="18" fontWeight="900" fill="#e8651a" textAnchor="middle">W</text>
            </svg>
          </div>
          {/* Dark right — wordmark only */}
          <div style={{background:C.bg,flex:1,height:"100%",display:"flex",alignItems:"center",padding:"0 14px"}}>
            <div style={{display:"flex",flexDirection:"column",gap:1}}>
              <span style={{fontSize:20,fontWeight:700,letterSpacing:"-0.5px",color:C.text,lineHeight:1}}>wazat</span>
              <span style={{fontSize:8,fontWeight:700,color:C.brand,letterSpacing:2,lineHeight:1}}>I WAS AT THAT SHOW</span>
            </div>
          </div>
        </div>

        {/* Buttons row */}
        <div style={{background:C.bg,display:"flex",alignItems:"center",justifyContent:"flex-end",gap:8,padding:"8px 16px"}}>
        {false&&<span style={{fontSize:11,fontWeight:700,background:C.brand,color:"#fff",borderRadius:20,padding:"2px 8px",letterSpacing:0.5}}>PRO</span>}
          <button onClick={()=>setShowMonetize(true)} style={{display:"none",background:"none",border:`1px solid ${C.border}`,borderRadius:10,padding:"7px 12px",color:isPro?C.gold:C.sub,fontSize:13,fontWeight:600,cursor:"pointer"}}>
            {isPro?"✦ Pro":"Plans"}
          </button>
          <button onClick={()=>setAdding(true)} style={{background:C.brand,border:"none",borderRadius:10,padding:"8px 16px",color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer"}}>+ Log</button>
        </div>

        {/* Sticky Tabs */}
        <div style={{display:"flex",padding:"0 20px",background:C.bg}}>
          {TABS.map(t=>(
            <button key={t.key} onClick={()=>setTab(t.key)} style={{flex:1,background:"none",border:"none",borderBottom:`2px solid ${tab===t.key?C.brand:"transparent"}`,padding:"10px 0",fontSize:13,fontWeight:tab===t.key?700:400,color:tab===t.key?C.brand:C.sub,cursor:"pointer",transition:"all 0.15s"}}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Scrollable Content */}
      <div style={{flex:1,overflowY:"auto",overflowX:"hidden",padding:"20px 20px 100px",WebkitOverflowScrolling:"touch"}}>
        {tab==="upcoming"&&<UpcomingTab onSelect={setSelectedConcert} location={location}/>}
        {tab==="shows"   &&<ShowsTab shows={shows} onSelect={setSelectedShow} onDelete={handleDelete} onEdit={setEditing} loading={showsLoading} goingShows={goingShows}/>}
        {tab==="friends" &&<FriendsTab onOpenMonetize={()=>setShowMonetize(true)} session={session}/>}
        {tab==="profile" &&<ProfileTab session={session} shows={shows} goingShows={goingShows} isPro={isPro} location={location} onLocationChange={setLocation} onOpenMonetize={()=>setShowMonetize(true)} onSignOut={()=>supabase.auth.signOut()} onShowGuide={()=>setShowOnboarding(true)}/>}
      </div>

      {/* Onboarding */}
      {showOnboarding&&<OnboardingSheet onClose={()=>setShowOnboarding(false)} onLog={()=>{setShowOnboarding(false);setAdding(true);}} onExplore={()=>{setShowOnboarding(false);setTab("upcoming");}}/>}

      {/* Sheets */}
      {selectedShow    &&<ShowSheet    show={selectedShow}    onClose={()=>setSelectedShow(null)}    onOpenMonetize={()=>setShowMonetize(true)}/>}
      {selectedConcert &&<ConcertSheet show={selectedConcert} onClose={()=>setSelectedConcert(null)} onOpenMonetize={()=>setShowMonetize(true)} onGoing={handleGoing} goingIds={goingShows.map(s=>s.id)}/>}
      {editing          &&<EditShowSheet show={editing} onClose={()=>setEditing(null)} onUpdate={handleUpdate}/>}
      {adding          &&<AddShowSheet onClose={()=>setAdding(false)} onAdd={s=>setShows(p=>[s,...p])} userId={session.user.id}/>}
      {showMonetize    &&<MonetizationSheet onClose={()=>setShowMonetize(false)}/>}
    </div>
  );
}


